// main.js – boot Firebird in a Worker, load the WAD into it, run the loop.
//
// Per frame the browser does exactly two queries:
//   SELECT * FROM doom_tic(...)        – advance the game by N tics
//   SELECT * FROM frame_walls / frame_sprites / frame_sectors – what to draw
// and then rasterises the rows. All game state lives in Firebird tables.

import { FirebirdBrowser } from 'firebird-wasm/browser';
import schemaSql from '../sql/schema.sql';
import gameSql from '../sql/game.sql';
import renderSql from '../sql/render.sql';
import { Wad } from './wad.js';
import { createSchema, loadResources, loadMap, setView, setRenderer } from './loader.js';
import { Renderer, viewGeometry } from './renderer.js';
import { drawStatusBar, FaceWidget, drawText, drawWeapon, extraLight } from './hud.js';
import { AM_COLORS, AM_STRINGS, AutomapView, GRID_COLOR, automapColor, automapPlayers } from './automap.js';
import { cheatReaders, clevMap, idmusMap } from './cheats.js';
import { nextMap } from './progress.js';
import { Finale, parseDehStrings, setFallbackStrings } from './finale.js';
import { Menu, TitleLoop } from './menu.js';
import { captureGame, restoreGame, saveStore, exportSaves, importSaves, rememberFiles, recallFiles, SLOTS,
  savePlayers, saveSetting, packSave, unpackSave, SAVE_VERSION } from './savegame.js';
import { DemoPlayer, DemoRecorder, demoProblem, attractDemoFile, ATTRACT_DEMOS } from './demo.js';
import { parseLmp, LmpPlayer } from './lmp.js';
import { Intermission, levelOf, setParOverrides } from './intermission.js';
import { DoomAudio, musicLumpFor } from './audio.js';
import { createPresenter } from './present.js';
import { Melt } from './wipe.js';
import { Lockstep, createInvite, acceptInvite, relayClient, isRoomCode, MAX_PLAYERS, SPECIAL_PAUSE, SPECIAL_SAVE } from './net.js';
import { Chat, chatStrings, HU_MSGTIMEOUT } from './chat.js';
import { MouseInput, moveCommand } from './mouse.js';
import { parseCommandLine, startMapFor } from './cmdline.js';
import { readJoystick, JoyMenu, JOYB } from './gamepad.js';
import { ACTIONS, keysFrom, bindKey, actionDown, boundCodes, keyName, reservedKey } from './keys.js';

const $ = (id) => document.getElementById(id);
let canvas = $('screen');   // replaced by a fresh element when the display kind changes
const statusEl = $('status');
const statsEl = $('stats');

const TIC_MS = 1000 / 35;

let db;
let wad;
let res;
let renderer;
let presenter = null; // WebGL (palette shader) or Canvas 2D: present.js
let map = null;      // { name, lines, sides, sectors, skyTex, linedefs }
let hud = null;      // last DOOM_TIC row
let sidesRev = -1;
let running = false;
let paused = false;
let lastTic = 0;
// settings, remembered per browser
const settings = { game: 'freedoom1', detail: 'high', renderer: 'bsp', audio: true, sfx: 70, music: 50, synth: 'opl2', display: 'webgl', smooth: false, skill: 3, messages: true, mouse: 5, mousey: false, screenSize: 10, gamma: 0 };
try {
  Object.assign(settings, JSON.parse(localStorage.getItem('firebird-doom:settings') || '{}'));
} catch { /* storage unavailable: defaults */ }
const saveSettings = () => {
  try { localStorage.setItem('firebird-doom:settings', JSON.stringify(settings)); } catch { /* ignore */ }
};
// R_SetViewSize: the menu's Screen Size (screenblocks 3–11) and the detail
const view = () => viewGeometry(settings.screenSize, settings.detail);
async function applyView() {
  const g = view();
  await setView(db, g.w, g.h, g.scaledW / 2);
  renderer?.setSize(g.w, g.h, g);
}
let showMap = false;
let face = new FaceWidget();         // the status bar face (ST_updateFaceWidget), its own state
let lastFire = false;               // the attack button, as the face sees it (player->attackdown)
let amView = null;                  // the automap's window: zoom, follow, grid, marks (AutomapView)
let lastSaid = null;                // the last of those (doom.lastSaid: it may time out before a slow test looks)
let amMsg = null;                   // the page's own messages ({ text, tics }: the automap's, F5/F8/F11's), shown like the game's but kept out of Firebird
let finale = null;                  // text screens: DOOM II's (MAP06/11/20, the secret levels, MAP30) and DOOM I's E1M8
let finaleKey = false;              // a key went down: F_CastResponder
let intermission = null;            // the stats screen between levels (wi_stuff.c)
let menu = null;                    // m_menu.c, for this WAD
let title = null;                   // the title loop (before a game, after End Game or Quit)
let menuBackdrop = null;            // the screen as it was when the menu opened over the game
let menuOpenedAt = -1e9;
let lastPalette = 0;
// D_Display's wipe: the screen melts (f_wipe.c) when the state changes, and on every level load
let levelSerial = 0;                 // (G_DoLoadLevel's wipegamestate = -1: a new level always melts)
let shownState = null;               // what the last screen presented showed
let shownScreen = null;              // …and that screen (wipe_StartScreen)
let melt = null;
let meltPalette = 0;
const meltBuf = new Uint8Array(320 * 200);
const screenState = () => (title && !attract ? 'title' : intermission ? 'intermission' : finale ? 'finale' : `level ${levelSerial}`);
const saves = saveStore();          // IndexedDB: six slots per WAD
let wadKey = '';                    // which WAD the saves belong to
let saveSlots = Array(SLOTS).fill(null);   // the slots' descriptions, for the menu
let recorder = null;                // a demo being recorded (demo.js)
let demoPlayer = null;              // …or played back
let demoNet = null;                 // a netgame's .lmp playing: { players, deathmatch, me } (no network: its ticcmds are all there)
/** whose eyes: the netgame's consoleplayer, a netgame demo's, or player 1 */
const viewer = () => net?.me ?? demoNet?.me ?? 1;
let lastDemo = null;                // the last one recorded or loaded, for Play and Download
let gameSkill = 0;                  // gameskill: the game's, kept from level to level (0: none yet)
let attract = 0;                    // the title loop's demo playing now (D_DoAdvanceDemo's DEMOn), 0 outside it
let attractDemos = new Map();       // this WAD's bundled demos, by number
let lastSeed = null;                // the seed the current map started from
let wiButtons = true;               // fire/use held last tic: only a new press accelerates
const didSecret = new Set();        // DOOM I episodes whose secret level is done (wbs->didsecret)
let amArrows = [];                  // the players' arrows the automap drew last ({ id, color }), for doom.automap
let amCheating = 0;                 // IDDT: 0, 1 (every line), 2 (…and every thing)
// ST_Responder's cheats, typed any time during play (respelt by a DeHackEd patch: cheatReaders)
let cheats = cheatReaders();
// D_DoomMain's command line, from the URL (cmdline.js): read once, at load
const commandLine = parseCommandLine(location.search);
const audio = new DoomAudio();
// (-nosfx, -nomusic, -nosound: silent for this visit, whatever the sliders say)
const applyVolumes = () => audio.setVolumes(commandLine.sound.sfx ? settings.sfx / 100 : 0, commandLine.sound.music ? settings.music / 100 : 0);
applyVolumes();
audio.setOpl3(settings.synth === 'opl3');
audio.setEnabled(settings.audio);
let lastSoundId = 0;
// audio may only start after a user gesture
for (const ev of ['keydown', 'pointerdown', 'touchstart']) window.addEventListener(ev, () => audio.unlock(), { capture: true });
document.addEventListener('visibilitychange', () => audio.suspend(document.hidden));
let lastFrame = { tic: 0, walls: 0, sprites: 0, draw: 0, rows: 0 };
// d_main.c's -nomonsters, -respawn, -fast: the checkboxes under the view (for this
// visit, as a command line is for one run), and the ones the game being played began with
const LAUNCH_OPTIONS = ['nomonsters', 'respawn', 'fast'];
const launchOptions = { nomonsters: 0, respawn: 0, fast: 0 };
let timedemo = null;                 // -timedemo: { start, frames, tics } while it runs, then the result
let gameOptions = { ...launchOptions };
let wallCache = null;                // FRAME_WALLS' last answer and what it answered ({ key, walls })
let wallsReused = 0;                 // frames drawn from it without asking again
// a netgame (net.js): { me, players, deathmatch, timer, ls: Lockstep, links: Map(player → link) }; null alone
let net = null;
// before it starts: the host's links so far and its open invite, or the guest's link
const lobby = { role: null, links: [], invite: null, guest: null };
let netWait = 0;                     // since when the netgame has been waiting for a tic

function setStatus(msg, isError = false) {
  statusEl.textContent = msg;
  statusEl.classList.toggle('error', isError);
  statusEl.hidden = !msg;
}

// ── input ────────────────────────────────────────────────────────────────
const keys = new Set();
let mouseTurn = 0;                   // (touch: the right half's drag)
let fireClick = false;               // (touch: a tap on the right half)
const mouse = new MouseInput();      // the mouse under pointer lock: its moves and DOOM's three buttons
let weaponSel = 0;
// default.cfg's keys (keys.js), remembered; the keys the page keeps from the browser follow them
let bindings = keysFrom(settings.keys);
let gameKeys = new Set();
const updateGameKeys = () => { gameKeys = new Set([...boundCodes(bindings), 'Tab', ...Array.from({ length: 7 }, (_, i) => `Digit${i + 1}`)]); };
updateGameKeys();
// the automap's own keys (AM_Responder): with it open they're its, not the game's
const AM_KEYS = new Set(['KeyF', 'KeyG', 'KeyM', 'KeyC', 'Digit0']);

window.addEventListener('keydown', (e) => {
  if (keyCapture) { captureKey(e); return; }
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
  if (!running) return;
  // M_Responder: the menu takes every key while it's open; on the title any
  // key opens it; in play Esc does. (Esc that just freed the mouse already did.)
  if (menu?.active) {
    e.preventDefault();
    if (!(e.key === 'Escape' && performance.now() - menuOpenedAt < 200)) menu.key(e.key);
    return;
  }
  // M_Responder's function keys, with the menu down
  if (/^F([1-9]|1[01])$/.test(e.key) && menu) {
    e.preventDefault();
    if (e.key === 'F5') { pageSay(menu.toggleDetail()); return; }
    if (e.key === 'F8') { pageSay(menu.toggleMessages(), true); return; }
    if (e.key === 'F11') { setGamma((settings.gamma + 1) % 5, true); return; }
    if ((e.key === 'F6' || e.key === 'F9') && demoPlayer) return;   // (in a netgame F6 saves for everyone, and F9 says it can't load)
    menuBackdrop = renderer.sfb.slice();
    keys.clear();
    ({ F1: () => menu.openReadThis(), F2: () => menu.openSave(), F3: () => menu.openLoad(), F4: () => menu.openSound(),
      F6: () => menu.quickSave(), F7: () => menu.endGame(), F9: () => menu.quickLoad(), F10: () => menu.quit() })[e.key]?.();
    return;
  }
  if (title && menu) {
    e.preventDefault();
    openMenu();
    return;
  }
  if (e.key === 'Escape' && demoPlayer) {
    e.preventDefault();
    endPlayback('stopped');
    return;
  }
  if (e.key === 'Escape' && menu) {
    e.preventDefault();
    openMenu();
    return;
  }
  // HU_Responder (G_Responder, in a level): T talks to everyone, G/I/B/R to one
  // player; while the line is open it takes what it can type, and the rest
  // still reaches the game. (Alt+digit by the key's place, whatever Alt makes it type.)
  if (net?.chat && !intermission && !finale) {
    const k = e.altKey && /^Digit\d$/.test(e.code) ? e.code.slice(5) : e.key;
    if (net.chat.key(k, e.altKey)) { e.preventDefault(); return; }
  }
  if (gameKeys.has(e.code)) e.preventDefault();
  keys.add(e.code);
  if (finale) finaleKey = true;
  if (e.code === 'Tab') showMap = !showMap;
  // M_Responder: - and = shrink and grow the view (M_SizeDisplay), unless the automap has them
  if (!showMap && (e.code === 'Minus' || e.code === 'Equal') && menu && !title) {
    e.preventDefault();
    sizeDisplay(e.code === 'Equal' ? 1 : -1);
  }
  // AM_Responder: F follow, G grid, M mark, C clear marks, 0 the whole level;
  // = and - zoom and (follow off) the arrows pan while held – and the game
  // doesn't see them (so F doesn't fire, nor M switch the sound off)
  if (showMap && amView && running && !menu?.active) {
    const said = { KeyF: () => amView.toggleFollow(), KeyG: () => amView.toggleGrid(), KeyM: () => amView.addMark(),
      KeyC: () => amView.clearMarks(), Digit0: () => amView.toggleBig() }[e.code]?.();
    if (said) amSay(said);
    // (the cheat readers below still see the key, as ST_Responder does before AM_Responder)
    if (['KeyF', 'KeyG', 'KeyM', 'KeyC', 'Digit0', 'Minus', 'Equal', 'NumpadAdd', 'NumpadSubtract'].includes(e.code)) e.preventDefault();
  }
  // AM_Responder: the automap listens for IDDT while it's open (not in deathmatch)
  if (showMap && cheats.iddt(e.key) && !(net ?? demoNet)?.deathmatch) amCheating = (amCheating + 1) % 3;
  for (const [code, read] of cheats.fixed) {
    if (recorder || demoPlayer || net) break;   // (a cheat isn't an input: it would desync the demo, or the netgame)
    if (read(e.key)) db.query(`EXECUTE PROCEDURE cheat('${code}')`).catch((err) => console.error(err));
  }
  // IDMUS xy: S_ChangeMusic to another level's song, if there is such a song
  const song = cheats.idmus(e.key);
  if (song && settings.skill !== 5 && !net) {   // (ST_Responder: not on Nightmare)
    const mapFor = idmusMap(song, wad.mapNames().some((m) => m.startsWith('MAP')));
    const lump = mapFor && musicLumpFor(mapFor);
    const ok = lump && wad.lump(lump);
    if (ok) audio.playMusic(lump);
    db.query(`UPDATE player SET msg = '${ok ? 'Music Change' : 'IMPOSSIBLE SELECTION'}', msg_tics = 70 WHERE id = 1`)
      .catch((err) => console.error(err));
  }
  // IDCLEV xy: G_DeferedInitNew – a new game on that map, if this WAD has it
  const digits = cheats.idclev(e.key);
  const warp = digits && clevMap(digits, wad.mapNames());
  if (warp && !recorder && !demoPlayer && !net) {
    $('map').value = warp;
    startMap(warp, true).catch((err) => setStatus(err.message, true));
  }
  if (e.code.startsWith('Digit')) weaponSel = Number(e.code.slice(5));
  // G_Responder's pause key: alone, the game stops here; in a netgame it goes out
  // as BTS_PAUSE in the next ticcmd, and every peer pauses (or goes on) on that tic
  if (e.code === 'KeyP' || e.code === 'Pause') {
    if (!net) paused = !paused;
    else if (!net.special) net.special = SPECIAL_PAUSE;
  }
  if (e.code === 'KeyM' && !showMap) setAudio(!settings.audio);
});
window.addEventListener('keyup', (e) => keys.delete(e.code));
window.addEventListener('blur', () => { keys.clear(); mouse.release(); });
function bindCanvas(c) {
  c.addEventListener('click', () => {
    // (some embedded browsers refuse pointer lock; the keyboard still works)
    if (running && document.pointerLockElement !== c) c.requestPointerLock?.()?.catch?.(() => {});
  });
  c.addEventListener('mousedown', (e) => {
    if (document.pointerLockElement !== c) return;
    e.preventDefault();
    mouse.button(e.button, true);
  });
  c.addEventListener('contextmenu', (e) => { if (document.pointerLockElement === c) e.preventDefault(); });
  c.addEventListener('touchstart', touchStart, { passive: false });
  c.addEventListener('touchmove', touchMove, { passive: false });
  c.addEventListener('touchend', touchEnd, { passive: false });
}
window.addEventListener('mouseup', (e) => mouse.button(e.button, false));
// letting go of the mouse in play (Esc, or switching away) brings up the menu, as Esc would
document.addEventListener('pointerlockchange', () => {
  if (!document.pointerLockElement && running && menu && !menu.active && !title) {
    openMenu();
    menuOpenedAt = performance.now();
  }
});

/** M_StartControlPanel: the menu over a frozen picture of the game */
function openMenu() {
  if (!menu || menu.active) return;
  menuBackdrop = renderer.sfb.slice();
  keys.clear();
  menu.open();
}

/** D_StartTitle: back to the title loop (End Game, Quit, and at boot) */
function goTitle() {
  if (net) leaveNet('you left the game');
  finale = null;
  intermission = null;
  if (attract) { demoPlayer = null; demoNet = null; attract = 0; }
  menu?.close(true);
  const maps = wad.mapNames();
  title = new TitleLoop(maps.some((m) => m.startsWith('MAP')), !!wad.lump('E4M1'), (m) => audio.playMusic(m));
}

/** G_RecordDemo: a fresh start of this map, a new seed, and every tic from now */
async function startRecording() {
  if (!map || !db) return;
  await startMap(map.name, true);
  recorder = new DemoRecorder({ wad: wadKey, map: map.name, skill: settings.skill, seed: lastSeed, ...gameOptions });
  setStatus(`● Recording a demo of ${map.name}`);
  updateDemoButtons();
}

/** G_CheckDemoStatus, recording: keep it (and in this WAD's slot store) */
function finishRecording() {
  if (!recorder) return;
  const { demo } = recorder;
  const { levels, tics } = recorder;
  recorder = null;
  if (demo.calls.length) {
    lastDemo = demo;
    saves.put(`${wadKey}|demo`, demo).catch(() => {});
    setStatus(`Demo recorded: ${levels.join(' → ')}, ${tics} tics. Play or Download it below.`);
  } else setStatus('');
  updateDemoButtons();
}

/** G_DoPlayDemo: the demo's map, skill and seed, then its inputs */
async function playDemo(demo) {
  const problem = demoProblem(demo);
  if (problem) { setStatus(`Can't play that: ${problem}`, true); return; }
  if (demo.wad !== wadKey) { setStatus(`That demo was recorded with ${demo.wad.split('|')[0]}; load that WAD first`, true); return; }
  if (!wad.mapNames().includes(demo.map)) { setStatus(`This WAD has no ${demo.map}`, true); return; }
  $('map').value = demo.map;
  await startMap(demo.map, true, { skill: demo.skill, seed: demo.seed, options: demoOptions(demo) });
  demoPlayer = new DemoPlayer(demo);
  setStatus(`▶ Playing a demo of ${demo.map} (Esc stops it)`);
  updateDemoButtons();
}

/**
 * G_DoPlayDemo for DOOM's own demos (.lmp, or a WAD's DEMOn lump): the header's skill, map and
 * options as G_InitNew sets them up (P_Random's index from 0), then a ticcmd a tic – through
 * deaths, the intermission and the levels after, as long as the demo goes. → whether it started.
 */
async function playLmp(bytes, name = 'the demo', { forTitle = 0 } = {}) {
  let player;
  try {
    player = new LmpPlayer(parseLmp(bytes), wad.mapNames().some((m) => m.startsWith('MAP')));
  } catch (err) {
    if (!forTitle) setStatus(`Can't play ${name}: ${err.message}`, true);
    return false;
  }
  const d = player.demo;
  if (!wad.mapNames().includes(player.map)) {
    if (!forTitle) setStatus(`Can't play ${name}: this WAD has no ${player.map}`, true);
    return false;
  }
  if (!forTitle) $('map').value = player.map;
  if (player.net && net) {
    if (!forTitle) setStatus(`Can't play ${name} during a netgame`, true);
    return false;
  }
  await startMap(player.map, true, { skill: d.skill, seed: 0, keepDemo: !!forTitle, attract: !!forTitle, fromFrame: !!forTitle,
    options: demoOptions(d), netDemo: player.net });
  if (forTitle && (attract !== forTitle || !title)) return true;   // (a game started meanwhile)
  demoPlayer = player;
  if (!forTitle) {
    const who = player.net ? `, ${player.net.deathmatch ? (player.net.deathmatch === 2 ? 'altdeath' : 'deathmatch') : 'co-op'} for ${player.net.players}, seen as player ${player.net.me}` : '';
    setStatus(`▶ Playing ${name}: ${player.map}, skill ${d.skill}${who}, ${d.tics.length} tics (Esc stops it)`);
  }
  updateDemoButtons();
  return true;
}

/** where a .lmp left the game, read before anything else runs (doom.demo.lmpEnd, for tests) */
let lmpEnd = null;
function noteLmpEnd(player, why) {
  lmpEnd = null;
  db.query(`SELECT g.tic, g.rng, t.x, t.y, t.z, t.angle, p.health, g.map_name FROM player p JOIN things t ON t.id = p.thing_id
      CROSS JOIN game g WHERE p.id = ${player.net?.me ?? 1} AND g.id = 1`)
    .then((r) => { lmpEnd = { ...r.rows[0], MAP_NAME: r.rows[0].MAP_NAME?.trim(), played: player.index, tics: player.demo.tics.length, why }; })
    .catch(() => {});
}

/** the launch options a demo was recorded with (none, in one from before they were kept) */
const demoOptions = (demo) => Object.fromEntries(LAUNCH_OPTIONS.map((o) => [o, demo[o] ? 1 : 0]));

function endPlayback(why) {
  if (demoPlayer?.lmp) noteLmpEnd(demoPlayer, why);
  demoNet = null;
  if (attract) { endAttract(); return; }
  if (demoPlayer?.lmp && !timedemo) {
    // G_CheckDemoStatus: DOOM's demo over, D_AdvanceDemo – back to the title loop
    demoPlayer = null;
    setStatus(`Demo ended: ${why}.`);
    goTitle();
    updateDemoButtons();
    return;
  }
  demoPlayer = null;
  if (timedemo && timedemo.ms == null && timedemo.start != null) {
    // G_CheckDemoStatus with -timedemo: how long it took, in tics and frames a second
    const ms = performance.now() - timedemo.start;
    timedemo = { tics: timedemo.tics, frames: timedemo.frames, ms: Math.round(ms), realtics: Math.round((ms * 35) / 1000),
      fps: Number(((timedemo.frames * 1000) / ms).toFixed(1)), why };
    setStatus(`Timedemo: ${timedemo.tics} game tics in ${timedemo.realtics} real tics (${(timedemo.ms / 1000).toFixed(1)} s), ${timedemo.fps} frames a second (${why}).`);
  } else setStatus(`Demo ended: ${why}.`);
  updateDemoButtons();
}

/**
 * The command line's start (D_DoomMain, after the title is up): a demo to play
 * or time, or with -warp, -episode or -skill a game at once (autostart),
 * recording if -record says so. Problems are said in the status line.
 */
async function runCommandLine() {
  const cl = commandLine;
  if (cl.unknown.length) console.info(`[firebird-doom] command line: ignoring ${cl.unknown.join(', ')}`);
  if (cl.netgame.deathmatch != null) $('net-mode').value = String(cl.netgame.deathmatch);
  if (cl.netgame.timer != null) $('net-timer').value = String(cl.netgame.timer);
  if (cl.problems.length) setStatus(`Command line: ${cl.problems.join('; ')}.`, true);
  if (cl.demo) {
    let demo = null;
    const lump = cl.demo.name !== 'last' && wad.lump(cl.demo.name.toUpperCase());
    if (lump) {
      // the WAD's own DEMOn (-playdemo demo1 in DOOM reads the lump)
      if (await playLmp(wad.data(lump), cl.demo.name.toUpperCase()) && cl.demo.timed && demoPlayer) timedemo = { start: null, frames: 0, tics: 0 };
      return;
    }
    if (cl.demo.name === 'last') demo = await saves.get(`${wadKey}|demo`).catch(() => null);
    else if (!pwads.length && !dehPatch) {
      const n = Number(cl.demo.name.slice(4));
      demo = await fetch(new URL(`./demos/${attractDemoFile(baseWad.label, n)}`, location.href))
        .then((r) => (r.ok ? r.json() : null)).catch(() => null);
    }
    if (!demo || demoProblem(demo)) { setStatus(`Command line: no ${cl.demo.name} for this WAD.`, true); return; }
    await playDemo(demo);
    if (cl.demo.timed && demoPlayer) timedemo = { start: null, frames: 0, tics: 0 };
    return;
  }
  let name;
  try { name = startMapFor(cl, wad.mapNames()); } catch (err) { setStatus(`Command line: ${err.message}.`, true); return; }
  if (!name) return;
  if (cl.skill) { settings.skill = cl.skill; $('skill').value = String(cl.skill); }
  $('map').value = name;
  if (cl.record) {
    await startMap(name, true);
    await startRecording();
  } else await startMap(name, true);
}

/**
 * D_DoAdvanceDemo's G_DeferedPlayDemo: the title loop has reached DEMOn. With
 * a bundled demo for this WAD, it plays (the title loop waits, melting in and
 * out like DOOM's wipe); without one – another WAD, or not fetched yet – the
 * loop moves on, where DOOM would stop for the missing lump.
 */
async function startAttract(n) {
  // the WAD's own DEMOn lump, as DOOM plays it (id's WADs, a PWAD's); else the port's own
  // recording for a bundled WAD (Freedoom has no demo lumps)
  const lump = wad.lump(`DEMO${n}`);
  if (lump && !net && !lobby.role) {
    attract = n;
    frameStarting = true;
    let ok = false;
    try { ok = await playLmp(wad.data(lump), `DEMO${n}`, { forTitle: n }); } finally { frameStarting = false; }
    if (!ok && attract === n) { attract = 0; title?.advance(); }
    return;
  }
  const demo = attractDemos.get(n);
  // (not while players are gathering for a netgame, or in one: the map is theirs)
  if (!demo || demo.wad !== wadKey || !wad.mapNames().includes(demo.map) || net || lobby.role) { title.advance(); return; }
  attract = n;
  frameStarting = true;
  try {
    await startMap(demo.map, true, { skill: demo.skill, seed: demo.seed, keepDemo: true, attract: true, fromFrame: true, options: demoOptions(demo) });
    if (attract !== n || !title) return;   // (a game started meanwhile)
    demoPlayer = new DemoPlayer(demo);
  } catch (err) {
    attract = 0;
    title?.advance();
    throw err;
  } finally { frameStarting = false; }
  updateDemoButtons();
}

/** the demo is over (or out of step): the title loop's next page, melting in over the level */
function endAttract() {
  demoPlayer = null;
  demoNet = null;
  attract = 0;
  title?.advance();
  updateDemoButtons();
}

function updateDemoButtons() {
  $('demo-record').disabled = !!recorder || (!!demoPlayer && !attract);
  $('demo-stop').disabled = !recorder && (!demoPlayer || !!attract);
  $('demo-play').disabled = !lastDemo || !!recorder;
  $('demo-download').disabled = !lastDemo;
}

/** The six slots of this WAD, as the menu lists them. */
async function refreshSlots() {
  const key = wadKey;
  const found = await Promise.all(Array.from({ length: SLOTS }, (_, i) => saves.get(`${key}|${i}`).catch(() => null)));
  if (key === wadKey) saveSlots = found.map((r) => (r ? { name: r.name, map: r.map, players: savePlayers(r.save) } : null));
  netSaves();
}

/** The panel's Start from: a new game, or one of this WAD's saves (-loadgame) */
function netSaves() {
  const sel = $('net-save');
  const was = sel.value;
  sel.replaceChildren(new Option('a new game', '-1'),
    ...saveSlots.flatMap((s, i) => (s ? [new Option(`save ${i + 1}: ${s.name || '(no name)'}, ${s.map}, ${s.players} player${s.players === 1 ? '' : 's'}`, String(i))] : [])));
  sel.value = [...sel.options].some((o) => o.value === was) ? was : '-1';
}

/** G_SaveGame: the live tables, plus the automap's seen lines and DOOM I's visited secret levels */
async function saveToSlot(slot, name) {
  // G_SaveGame in a netgame: not now, but as BTS_SAVEGAME in the next ticcmd, so
  // every peer saves the same tic into the same slot (G_Ticker, then G_DoSaveGame)
  if (net) {
    if (!net.special) { net.special = SPECIAL_SAVE + slot; net.saveName = name; }
    return;
  }
  await writeSave(slot, name);
  await db.exec("UPDATE player SET msg = 'Game saved.', msg_tics = 70 WHERE id = 1");
}

/** G_DoSaveGame: the game as it stands, into SLOT of this browser's saves */
async function writeSave(slot, name) {
  const save = await captureGame(db, { seen: [...map.seen], didSecret: [...didSecret] });
  await saves.put(`${wadKey}|${slot}`, { name, map: save.map, date: new Date().toISOString(), save });
  await refreshSlots();
}

/** G_LoadGame: the map afresh, then the save written over it */
async function loadFromSlot(slot) {
  const rec = await saves.get(`${wadKey}|${slot}`);
  if (!rec?.save) return;
  const { save } = rec;
  settings.skill = save.skill;
  saveSettings();
  $('skill').value = String(save.skill);
  $('map').value = save.map;
  await startMap(save.map, true);
  running = false;                 // (nothing ticks until the save is back)
  if (inFrame) await inFrame.catch(() => {});
  try {
    await restoreGame(db, save);
    gameOptions = Object.fromEntries(LAUNCH_OPTIONS.map((o) => [o, saveSetting(save, o) ? 1 : 0]));
    map.seen = new Set(save.extra?.seen ?? []);
    didSecret.clear();
    for (const e of save.extra?.didSecret ?? []) didSecret.add(e);
    await loadSides();
  } catch (err) {
    setStatus(`Couldn't load that save: ${err.message}`, true);
  }
  resume();
}

/** M_SizeDisplay: one step of the Screen Size, 3–11 */
function sizeDisplay(dir) {
  const next = Math.max(3, Math.min(11, settings.screenSize + dir));
  if (next === settings.screenSize) return;
  settings.screenSize = next;
  saveSettings();
  audio.playEvents([[0, 'DSSTNMOV', 'menu', null, null]], { x: 0, y: 0, angle: 0 });
  applyView().catch((err) => setStatus(err.message, true));
}

/** This WAD's menu, its options wired to the settings */
function makeMenu() {
  const maps = wad.mapNames();
  const doom2 = maps.some((m) => m.startsWith('MAP'));
  const strings = parseDehStrings(wad.dehacked() + '\n' + (dehPatch?.text ?? ''));
  const quitSounds = ['DSPLDETH', 'DSDMPAIN', 'DSPOPAIN', 'DSSLOP', 'DSTELEPT', 'DSPOSIT1', 'DSPOSIT3', 'DSSGTATK'];
  const play = (lump) => audio.playEvents([[0, lump, 'menu', null, null]], { x: 0, y: 0, angle: 0 });
  menu = new Menu({
    doom2,
    episodes: [1, 2, 3, 4].filter((e) => maps.includes(`E${e}M1`)).length || 1,
    retail: !!wad.lump('E4M1'),
    strings,
    sound: play,
    actions: {
      newGame(episode, skill) {
        settings.skill = skill;
        saveSettings();
        $('skill').value = String(skill);
        const first = doom2 ? (maps.includes('MAP01') ? 'MAP01' : maps[0]) : `E${episode}M1`;
        $('map').value = first;
        startMap(first, true).catch((err) => setStatus(err.message, true));
      },
      endGame: () => goTitle(),
      get slots() { return saveSlots; },
      // (only in a game: not on the title, the intermission or an ending)
      get canSave() { return !!map && !title && !intermission && !finale; },
      get inGame() { return !!map && !title; },     // (usergame: not the title loop, nor its demos)
      get netgame() { return !!net; },
      save: (slot, name) => saveToSlot(slot, name).catch((err) => setStatus(`Couldn't save: ${err.message}`, true)),
      load: (slot) => loadFromSlot(slot).catch((err) => setStatus(`Couldn't load: ${err.message}`, true)),
      quit() {
        play(quitSounds[Math.floor(Math.random() * quitSounds.length)]);
        goTitle();
      },
      // (the menu's thermometer counts 0–8; the menu plays its own slider sound)
      get screenSize() { return settings.screenSize - 3; },
      set screenSize(v) {
        settings.screenSize = v + 3;
        saveSettings();
        applyView().catch((err) => setStatus(err.message, true));
      },
      get messages() { return settings.messages; },
      set messages(v) { settings.messages = v; saveSettings(); },
      get detail() { return settings.detail; },
      set detail(v) { $('detail').value = v; $('detail').dispatchEvent(new Event('change')); },
      get mouse() { return settings.mouse; },
      set mouse(v) { settings.mouse = v; saveSettings(); },
      // the 0–15 thermometers on the 0–100 sliders
      get sfx() { return Math.round((settings.sfx * 15) / 100); },
      set sfx(v) { $('sfxvol').value = Math.round((v * 100) / 15); $('sfxvol').dispatchEvent(new Event('input')); },
      get music() { return Math.round((settings.music * 15) / 100); },
      set music(v) { $('musicvol').value = Math.round((v * 100) / 15); $('musicvol').dispatchEvent(new Event('input')); },
    },
  });
}
window.addEventListener('mousemove', (e) => {
  // (mouse sensitivity 0–9 from the Options menu; 5 is the old fixed rate)
  if (document.pointerLockElement === canvas) mouse.move(e.movementX, e.movementY, settings.mouse);
});

// Touch: left half moves, right half turns, tap on the right fires.
const touch = { move: null, look: null };
function touchStart(e) {
  for (const t of e.changedTouches) {
    const r = canvas.getBoundingClientRect();
    const left = t.clientX - r.left < r.width / 2;
    const rec = { id: t.identifier, x: t.clientX, y: t.clientY, dx: 0, dy: 0, t: performance.now() };
    if (left) touch.move = rec; else touch.look = rec;
  }
  e.preventDefault();
}
function touchMove(e) {
  for (const t of e.changedTouches) {
    for (const k of ['move', 'look']) {
      const rec = touch[k];
      if (rec && rec.id === t.identifier) {
        if (k === 'look') mouseTurn -= (t.clientX - rec.x - rec.dx) * 0.01;
        rec.dx = t.clientX - rec.x;
        rec.dy = t.clientY - rec.y;
      }
    }
  }
  e.preventDefault();
}
function touchEnd(e) {
  for (const t of e.changedTouches) {
    for (const k of ['move', 'look']) {
      const rec = touch[k];
      if (rec && rec.id === t.identifier) {
        if (k === 'look' && Math.abs(rec.dx) < 10 && performance.now() - rec.t < 250) fireClick = 'tap';
        if (k === 'move' && Math.abs(rec.dx) < 10 && Math.abs(rec.dy) < 10 && performance.now() - rec.t < 250) keys.add('TapUse');
        touch[k] = null;
      }
    }
  }
}
bindCanvas(canvas);

/**
 * Put the chosen presenter on a fresh canvas (a canvas keeps whichever kind of
 * context it gave out first). WebGL that can't be had falls back to 2D.
 */
function applyDisplay() {
  const fresh = () => {
    const c = canvas.cloneNode(false);
    canvas.replaceWith(c);
    canvas = c;
    bindCanvas(c);
    return c;
  };
  presenter = createPresenter(fresh(), settings.display);
  if (!presenter && settings.display !== '2d') presenter = createPresenter(fresh(), '2d');
  presenter?.setSmooth(settings.smooth);
  renderer?.attach(presenter);
  $('display-kind').textContent = presenter?.kind === 'webgl' ? 'WebGL' : presenter ? 'Canvas 2D' : 'none';
}

function readInput(tics) {
  const k = (c) => keys.has(c);
  const panning = showMap && amView && !amView.follow;   // (the arrows pan the map instead)
  // the bound keys (keys.js); the automap keeps its own keys while it's open
  const skip = (c) => (panning && c.startsWith('Arrow')) || (showMap && AM_KEYS.has(c));
  const on = (a) => actionDown(bindings, a, keys, skip);
  let fwd = (on('forward') ? 1 : 0) - (on('back') ? 1 : 0);
  let side = (on('straferight') ? 1 : 0) - (on('strafeleft') ? 1 : 0);
  let turnKeys = (on('left') ? 1 : 0) - (on('right') ? 1 : 0);
  let run = on('speed') ? 1 : 0;
  const strafeKey = on('strafe');
  // G_BuildTiccmd's joystick (gamepad.js): Y walks, X turns – or strafes while
  // joybstrafe is held – and its buttons fire, use and run, as the keys do
  const js = readJoystick(gamepads());
  if (js) {
    fwd = Math.max(-1, Math.min(1, fwd - js.y));
    if (js.buttons[JOYB.speed]) run = 1;
  }
  if (touch.move) {
    fwd = Math.max(-1, Math.min(1, -touch.move.dy / 40));
    side = Math.max(-1, Math.min(1, touch.move.dx / 40));
  }
  // G_BuildTiccmd's mouse: button 2 held strafes (the turn keys too), button 3
  // moves forward, a double click on either uses; the X turns or strafes, the Y
  // (if chosen) walks. Then forward and side clamp to MAXPLMOVE
  const m = mouse.take(tics, { run, mousey: settings.mousey, strafe: strafeKey });
  // (strafe = key_strafe || mousebuttons[mousebstrafe] || joybuttons[joybstrafe]: any of
  // them turns the turn keys and the stick's X into strafing)
  if (m.strafe || js?.buttons[JOYB.strafe]) {
    side = Math.max(-1, Math.min(1, side - turnKeys + (js?.x ?? 0)));
    turnKeys = 0;
  } else if (js?.x) turnKeys = Math.max(-1, Math.min(1, turnKeys - js.x));
  if (m.forward || m.side) ({ fwd, side, run } = moveCommand(fwd * (run ? 50 : 25) + m.forward, side * (run ? 50 : 25) + m.side, run));
  // angleturn 640/1280 per tic in DOOM ≈ 0.061 / 0.123 rad
  const turn = turnKeys * (run ? 0.123 : 0.07) * tics + mouseTurn + m.turn;
  mouseTurn = 0;
  const fire = on('fire') || fireClick || m.fire || js?.buttons[JOYB.fire] ? 1 : 0;
  if (fireClick === 'tap') fireClick = false;
  const use = on('use') || k('TapUse') || m.use || js?.buttons[JOYB.use] ? 1 : 0;
  keys.delete('TapUse');
  const w = weaponSel;
  weaponSel = 0;
  return [tics, fwd, side, turn, fire, use, w, run];
}

// ── maps ─────────────────────────────────────────────────────────────────
function skyFor(name) {
  const m = /^E(\d)M/.exec(name);
  const n = m ? Math.min(4, Number(m[1])) : Number(name.slice(3)) < 12 ? 1 : Number(name.slice(3)) < 21 ? 2 : 3;
  return res.texId.get(`SKY${n}`) ?? res.texId.get('SKY1') ?? 0;
}

async function loadSides() {
  const { rows } = await db.query('SELECT id, xoff, yoff, upper_tex, lower_tex, mid_tex, sector_id FROM sidedefs', [], { rowMode: 'array' });
  map.sides = new Map(rows.map((r) => [r[0], { xoff: r[1], yoff: r[2], upper: r[3], lower: r[4], mid: r[5], sector: r[6] }]));
}

/** One map load at a time: a second start waits for the first and lands last. */
let mapQueue = Promise.resolve();
function startMap(name, newGame, opts) {
  const run = mapQueue.then(() => startMapNow(name, newGame, opts));
  mapQueue = run.catch(() => {});
  return run;
}

async function startMapNow(name, newGame, { netDemo = undefined, skill = null, seed = null, reborn = false, keepDemo = false, netgame = false, attract: forTitle = false, fromFrame = false, restore = null, options = null } = {}) {
  if (net && !netgame) leaveNet('you started another game');
  running = false;
  // (a start from outside the loop – the menu, the panel, a message – waits for
  // the frame in flight: its queries must be done before the tables go. Not
  // for a frame that's waiting on a start of its own, queued behind this one.)
  if (inFrame && !fromFrame && !frameStarting) await inFrame.catch(() => {});
  if (forTitle && !attract) return;   // (the title loop's demo, called off while it waited its turn)
  levelSerial++;
  finale = null;
  intermission = null;
  if (!forTitle) {
    title = null;
    attract = 0;
    menu?.close(true);
  }
  // a map you pick (a new game, a save, the Map selector) ends a demo; the game's
  // own next level doesn't (nextLevel); demo starts set theirs afterwards
  if (!keepDemo) {
    if (recorder) finishRecording();
    demoPlayer = null;
  }
  // a netgame demo's players and view: set by its start, kept through its levels, gone with it
  if (netDemo !== undefined) demoNet = netDemo;
  else if (!keepDemo) demoNet = null;
  if (newGame) didSecret.clear();
  // the skill: the one asked for, or within a game (the next level, a restart) the game's own
  // (gameskill, as G_DoLoadLevel keeps it), else the setting's
  skill = skill ?? (keepDemo && gameSkill ? gameSkill : settings.skill);
  gameSkill = skill;
  setStatus(`Loading ${name} into Firebird…`);
  const t0 = performance.now();
  // P_Random's index: a demo's own seed, or 0 for a new game as G_InitNew's M_ClearRandom
  // sets it (a new level, and the restart after a death, G_DoReborn, carry on)
  const s = seed ?? (newGame && !reborn ? 0 : null);
  // the launch options: a demo's own, a netgame's, the game's as it goes from level to
  // level (G_DoLoadLevel keeps them), or the checkboxes' for a game starting now
  gameOptions = options ?? (net ? { nomonsters: net.nomonsters, respawn: net.respawn, fast: net.fast }
    : keepDemo ? gameOptions : { ...launchOptions });   // (keepDemo: nextLevel, the next map or this one after a death)
  await loadMap(db, wad, res, name, {
    skill, newGame, players: net?.players ?? demoNet?.players ?? 1, deathmatch: net?.deathmatch ?? demoNet?.deathmatch ?? 0,
    timer: net?.timer ?? 0, ...gameOptions, seed: s,
  });
  await db.exec(`UPDATE viewcfg SET player_id = ${viewer()} WHERE id = 1`);   // (consoleplayer)
  lastSeed = s;
  map = { name, skyTex: skyFor(name) };
  const { rows } = await db.query(
    'SELECT id, front_side, back_side, flags, light_delta, x1, y1, x2, y2, front_sector, back_sector, special FROM linedefs',
    [], { rowMode: 'array' });
  map.lines = new Map(rows.map((r) => [r[0], { fs: r[1], bs: r[2], flags: r[3], lightDelta: r[4], scroll: r[11] === 48 }]));
  map.linedefs = rows;
  map.seen = new Set();   // ML_MAPPED: every line the renderer has drawn on this level
  face = new FaceWidget();   // (ST_Start: the face starts over with each level)
  // AM_LevelInit: the level's extent, the window on it, no marks
  const xs = rows.flatMap((r) => [r[5], r[7]]);
  const ys = rows.flatMap((r) => [r[6], r[8]]);
  amView = new AutomapView({ minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) },
    wad.blockmapOrigin(name) ?? undefined);
  // a netgame from a save (-loadgame): written back over the map before any tic runs
  if (restore) {
    await restoreGame(db, restore);
    map.seen = new Set(restore.extra?.seen ?? []);
    for (const e of restore.extra?.didSecret ?? []) didSecret.add(e);
  }
  await loadSides();
  sidesRev = -1;
  console.log(`[firebird-doom] ${name} loaded in ${(performance.now() - t0).toFixed(0)} ms`);
  setStatus('');
  $('mapname').textContent = name;
  audio.playMusic(musicLumpFor(name));
  resume();
}

/**
 * G_DoLoadLevel within a game: the next level, or this one again after a
 * death. A demo goes on through it: recording notes the level and its seed,
 * playing takes them from the recording (a restart's seed is a new one).
 */
async function nextLevel(name, newGame, reborn = false) {
  let seed = null;
  if (demoPlayer) {
    const c = demoPlayer.take('map');
    if (c && (c[1] === name || c[1] === '*')) seed = c[2];
    else endPlayback(demoPlayer.done ? 'the demo is over' : 'it is out of step with the game');
  }
  const rec = recorder;
  const lmp = demoPlayer?.lmp && !reborn ? demoPlayer : null;
  frameStarting = true;
  try {
    // (the title loop's demo stays the title loop's through its next level)
    await startMap(name, newGame, { seed, reborn, keepDemo: true, netgame: !!net, fromFrame: true, attract: !!attract });
  } finally { frameStarting = false; }
  // (G_DoWorldDone: player->attackdown and usedown as the intermission left them)
  if (lmp && demoPlayer === lmp) {
    await db.query('UPDATE player p SET attack_down = ?, use_down = ? WHERE p.id = 1', [lmp.attackdown ? 1 : 0, lmp.usedown ? 1 : 0]);
  }
  rec?.push(['map', name, lastSeed, newGame ? 1 : 0]);
}

/** A demo being played whose next entry isn't KIND has run out, or out of step: stop it. Returns the entry. */
function demoTake(kind) {
  if (!demoPlayer) return null;
  const c = demoPlayer.take(kind);
  if (!c) endPlayback(demoPlayer.done ? 'the demo is over'
    : demoPlayer.net ? "a netgame's demo stops at the level's end here" : 'it is out of step with the game');
  return c;
}

// ── co-op over the network ────────────────────────────────────────────────
const IDLE_CMD = [0, 0, 0, 0, 0, 0, 0];

/**
 * One frame of a netgame: a command for each tic that has gone by (none while
 * the menu is up), then every tic the lockstep has complete, through NET_TIC.
 * False when no tic ran: nothing new to draw.
 */
async function netTics(tics) {
  const [, fwd, side, turn, fire, use, w, run] = readInput(tics);
  if (net.ls.error) return false;
  const mine = menu?.active ? IDLE_CMD : [fwd, side, turn / tics, fire, use, w, run];
  // (G_BuildTiccmd: each tic's command carries the next chat character, cmd->chatchar)
  for (let i = 0; i < tics && net.ls.canSubmit(); i++) {
    const cmd = [...(i === 0 ? mine : [...mine.slice(0, 5), 0, mine[6]]), net.chat.dequeue(), net.special];
    // (BT_SPECIAL: cmd->buttons = BT_SPECIAL | …, so no fire, use or weapon change that tic)
    if (net.special) { cmd[3] = 0; cmd[4] = 0; cmd[5] = 0; net.special = 0; }
    net.ls.submit(cmd);
  }
  let ran = 0;
  while (ran < 12) {
    const r = net.ls.take();
    if (!r) break;
    // (from the network: numbers, and only numbers, go into the SQL)
    const num = (v, lo, hi) => Math.max(lo, Math.min(hi, Number(v) || 0));
    const rows = r.cmds.map((c, i) => [i + 1, num(c[0], -1, 1), num(c[1], -1, 1), num(c[2], -Math.PI, Math.PI),
      num(c[3], 0, 1) | 0, num(c[4], 0, 1) | 0, num(c[5], 0, 9) | 0, num(c[6], 0, 1) | 0]);
    // G_Ticker's BT_SPECIAL, player by player: BTS_PAUSE flips the pause for
    // everyone; BTS_SAVEGAME saves once this tic is done (ga_savegame, acted on
    // before the next). A paused tic still goes round, so a pause can end, but
    // the world stands still (P_Ticker: if (paused) return)
    let saveSlot = -1;
    let saver = 0;
    r.cmds.forEach((c, i) => {
      const sp = num(c[8], 0, SPECIAL_SAVE + SLOTS - 1) | 0;
      if (sp === SPECIAL_PAUSE) net.paused = !net.paused;
      else if (sp >= SPECIAL_SAVE) { saveSlot = sp - SPECIAL_SAVE; saver = i + 1; }
    });
    if (!net.paused) {
      await db.query(`EXECUTE BLOCK AS BEGIN DELETE FROM ticcmd;
        ${rows.map((v) => `INSERT INTO ticcmd (player_id, fwd, side, turn, fire, use_key, weapon_sel, run) VALUES (${v.join(', ')});`).join('\n')} END`);
      hud = (await db.query('SELECT * FROM net_tic', [], { rowMode: 'object' })).rows[0];
      lastFire = rows[net.me - 1][4] === 1;
    }
    net.chat.ticker(r.cmds.map((c) => num(c[7], 0, 127) | 0));   // (HU_Ticker)
    if (saveSlot >= 0) {
      // (G_DoSaveGame: the one who saved names it; on the other machines it's a net game)
      await writeSave(saveSlot, saver === net.me ? net.saveName || NETGAME_SAVE : NETGAME_SAVE);
      pageSay('Game saved.');
    }
    ran++;
    // the consistency check (d_net.c's consistancy): the host compares everyone's
    if (r.tic % 35 === 0) {
      const sum = (await db.query('SELECT csum FROM net_checksum')).rows[0].CSUM;
      net.ls.report(r.tic, sum);
      net.sums.set(r.tic, sum);           // (the last few, for doom.net)
      if (net.sums.size > 8) net.sums.delete(net.sums.keys().next().value);
    }
    if (hud.EXIT_KIND) break;
  }
  if (net.ls.error) {
    setStatus(`Co-op stopped: ${net.ls.error}.`, true);
    netPanel();
    return false;
  }
  if (ran) {
    if (netWait && statusEl.textContent.startsWith('Waiting')) setStatus('');
    netWait = 0;
  } else if (!netWait) netWait = performance.now();
  else if (performance.now() - netWait > 1000) setStatus('Waiting for the other players…');
  return ran > 0 && !!hud;
}

/** A save made by another player of the netgame, as G_DoSaveGame names it when savedescription is empty */
const NETGAME_SAVE = 'NETGAME';

/** WI_Start's wbs->plyr[]: every player's kills, items, secrets and frags, for the netgame screens */
async function netStats() {
  const arr = { rowMode: 'array' };
  const rows = (await db.query('SELECT p.id, p.kills, p.items, p.secrets FROM player p ORDER BY p.id', [], arr)).rows;
  const frags = (await db.query('SELECT killer, victim, n FROM frags', [], arr)).rows;
  const players = rows.map((r) => ({ kills: r[1], items: r[2], secrets: r[3], frags: rows.map(() => 0) }));
  for (const [k, v, n] of frags) if (players[k - 1] && v <= players.length) players[k - 1].frags[v - 1] = n;
  return { players, me: net.me, deathmatch: net.deathmatch };
}

/**
 * G_InitNew for a netgame: everyone the same map, skill, seed and options
 * (-nomonsters, -respawn, -fast); me is this browser's player.
 */
async function beginNet({ me, players, mapName, skill, seed, deathmatch = 0, timer = 0, nomonsters = 0, respawn = 0, fast = 0, save = null }, links) {
  if (recorder) finishRecording();
  demoPlayer = null;
  paused = false;
  const send = (to, m) => {
    if (to === 'all') for (const l of links.values()) l.send(m);
    else links.get(to)?.send(m);
  };
  const strings = chatStrings(parseDehStrings(wad.dehacked() + '\n' + (dehPatch?.text ?? '')));
  const chat = new Chat({ me, players, strings, say: chatSay });
  amCheating = 0;   // (a netgame starts a fresh DOOM: no IDDT carried over from a game alone)
  net = { me, players, deathmatch, timer, nomonsters, respawn, fast, links, chat, ls: new Lockstep({ me, players, send }), sums: new Map(),
    special: 0, saveName: '', paused: false };
  netWait = 0;
  for (const [pid, link] of links) {
    link.onmessage = (m) => {
      // (a goodbye: nothing more goes down that link, which its sender is closing)
      if (m?.t === 'desync') link.dead = true;
      if (net?.links === links) net.ls.receive(pid, m);
    };
    link.onclose = () => {
      if (net?.links !== links || net.ls.error) return;
      const why = pid === 1 ? 'the host left' : `player ${pid} left`;
      // (to everyone else: not down the link that just closed)
      if (me === 1) for (const [q, l] of links) if (q !== pid) l.send({ t: 'desync', why });
      net.ls.fail(why);
    };
  }
  settings.skill = skill;
  $('skill').value = String(skill);
  $('map').value = mapName;
  await startMap(mapName, true, { skill, seed, netgame: true, restore: save });
  $('net-out').value = '';
  $('net-in').value = '';
  setStatus(`${deathmatch ? 'Deathmatch' : 'Co-op'}: you are player ${me} of ${players}.`);
  setTimeout(() => { if (/^(Co-op|Deathmatch): you/.test(statusEl.textContent)) setStatus(''); }, 4000);
  netPanel();
}

/** Out of the netgame (and the lobby): the links closed, back to one player at the next start. */
function leaveNet(why) {
  const links = net ? [...net.links.values()] : [...lobby.links.map((l) => l.link), lobby.guest].filter(Boolean);
  // a hang-up: the reason first, and the channel closed a second later, once the
  // others have stopped sending on it (WebKit reports a send that crosses a close
  // as a page error, whatever catches it)
  if (net && !net.ls.error) {
    for (const l of links) l.send({ t: 'desync', why: `player ${net.me} left` });
    for (const l of links) { l.dead = true; setTimeout(() => l.close(), 1000); }
  } else for (const l of links) l.close();
  lobby.invite?.cancel();
  Object.assign(lobby, { role: null, links: [], invite: null, guest: null, room: null });
  if (net) console.info(`[firebird-doom] co-op over: ${why}`);
  net = null;
  netPanel();
}

/** The Co-op panel's buttons and words, for where things stand */
function netPanel(say) {
  const host = lobby.role === 'host';
  $('net-host').disabled = !!net || lobby.role === 'guest' || !!lobby.invite || lobby.links.length >= MAX_PLAYERS - 1;
  $('net-join').disabled = !!net || !!lobby.role;
  $('net-start').disabled = !!net || !host || !lobby.links.length;
  $('net-leave').disabled = !net && !lobby.role;
  $('net-connect').disabled = !!net || !(lobby.invite || (lobby.role === 'guest' && !lobby.guest && !lobby.replied));
  if (say !== undefined) $('net-status').textContent = say;
  else if (net) $('net-status').textContent = net.ls.error ? `Stopped: ${net.ls.error}.` : `In the ${net.deathmatch ? 'deathmatch' : 'game'}: player ${net.me} of ${net.players}.`;
}

// The netgame relay (worker/relay.js): ?relay=URL, else the panel's Relay field,
// else the site's own (relay.json, written by the build from RELAY_URL); none: codes by hand
let siteRelay = '';
fetch(new URL('./relay.json', location.href)).then((r) => (r.ok ? r.json() : null))
  .then((d) => { if (typeof d?.url === 'string') { siteRelay = d.url; showRelay(); } }).catch(() => {});
const relayUrl = () => new URLSearchParams(location.search).get('relay') ?? settings.relay ?? siteRelay;
const relay = () => (relayUrl() ? relayClient(relayUrl()) : null);
function showRelay() { $('net-relay').value = relayUrl(); }
showRelay();
$('net-relay').addEventListener('change', (e) => {
  settings.relay = e.target.value.trim();
  saveSettings();
});
/** the relay's ICE servers (its TURN among them), or the page's own when there's none or it doesn't answer */
async function iceServers() {
  const r = relay();
  if (!r) return undefined;
  try { return await r.iceServers(); } catch (err) {
    console.warn('[firebird-doom] the relay gave no ICE servers:', err.message);
    return undefined;
  }
}

/** (host) a guest's reply: the link, as player 2, 3 or 4 */
async function hostAccept(reply) {
  netPanel('Connecting…');
  const link = await lobby.invite.accept(reply);
  lobby.invite = null;
  lobby.room = null;
  const pid = lobby.links.length + 2;
  lobby.links.push({ pid, link });
  link.onclose = () => {
    lobby.links = lobby.links.filter((l) => l.link !== link);
    if (!net) netPanel(`Player ${pid} went away.`);
  };
  $('net-out').value = '';
  $('net-in').value = '';
  netPanel(`${lobby.links.length + 1} players. Invite another, or Start (${$('map').value}, skill ${settings.skill}).`);
}

$('net-host').addEventListener('click', async () => {
  try {
    lobby.role = 'host';
    netPanel('Making an invite…');
    const invite = await createInvite({ iceServers: await iceServers() });
    lobby.invite = invite;
    $('net-in').value = '';
    const r = relay();
    let room = null;
    if (r) {
      try { room = await r.open(invite.code); } catch (err) {
        netPanel(`The relay didn't take the invite (${err.message}): send the code above instead, and paste their reply.`);
      }
    }
    if (!room) {
      $('net-out').value = invite.code;
      if (!r) netPanel(`Send the code above to player ${lobby.links.length + 2}, then paste their reply and press Connect.`);
      return;
    }
    // the room code is all the guest needs; their reply comes back through the relay
    lobby.room = room;
    $('net-out').value = room;
    netPanel(`Send the room code ${room} to player ${lobby.links.length + 2}: they press Join, paste it and Connect. Waiting for them…`);
    const reply = await r.waitReply(room, () => lobby.invite === invite);
    if (reply && lobby.invite === invite) await hostAccept(reply);
  } catch (err) { netPanel(`Couldn't make an invite: ${err.message}`); }
});
$('net-join').addEventListener('click', () => {
  lobby.role = 'guest';
  lobby.replied = false;
  $('net-out').value = '';
  $('net-in').value = '';
  netPanel(relay() ? "Paste the host's room code (or invite) below and press Connect." : "Paste the host's invite below and press Connect.");
});
$('net-connect').addEventListener('click', async () => {
  const code = $('net-in').value.trim();
  if (!code) return;
  try {
    if (lobby.role === 'host' && lobby.invite) {
      await hostAccept(code);   // (a reply pasted by hand, relay or not)
    } else if (lobby.role === 'guest') {
      // a room code (six letters and digits) is the relay's; anything longer is an invite
      const room = code.replace(/[\s-]/g, '').toUpperCase();
      const r = relay();
      let invite = code;
      if (isRoomCode(room)) {
        if (!r) throw new Error('that is a room code: it needs the relay the host uses (the Relay field)');
        netPanel(`Joining room ${room}…`);
        invite = await r.invite(room);
      }
      netPanel('Making a reply…');
      const { reply, link } = await acceptInvite(invite, { iceServers: await iceServers() });
      lobby.replied = true;
      if (invite !== code) {
        await r.reply(room, reply);
        $('net-out').value = '';
        netPanel(`In room ${room}: connecting to the host…`);
      } else {
        $('net-out').value = reply;
        netPanel('Send the reply above to the host, and wait for them to connect and start.');
      }
      const l = await link;
      lobby.guest = l;
      netPanel(`Connected. Waiting for the host to start (load the same WAD: ${$('wadname').textContent}).`);
      l.onmessage = async (m) => {
        if (m.t !== 'start' || net) return;
        // a netgame from the host's save: unpacked and checked before anything else
        let save = null;
        if (m.save !== undefined) {
          try {
            if (typeof m.save !== 'string' || m.save.length > NET_SAVE_MAX) throw new Error('too big');
            save = await unpackSave(m.save);
            if (save?.version !== SAVE_VERSION || save.map !== m.map || save.skill !== m.skill || savePlayers(save) !== m.players) throw new Error('not this game');
          } catch (err) {
            l.send({ t: 'desync', why: `player ${m.you} couldn't read the host's save (${err.message})` });
            netPanel(`The host's save didn't arrive whole: ${err.message}.`);
            return;
          }
        }
        // (from the network: check it all before it goes anywhere near the game)
        const ok = m.wad === wadKey && wad.mapNames().includes(m.map) && [1, 2, 3, 4, 5].includes(m.skill)
          && Number.isInteger(m.seed) && Number.isInteger(m.you) && m.you >= 2 && m.you <= m.players && m.players <= MAX_PLAYERS
          && [0, 1, 2].includes(m.dm) && Number.isInteger(m.timer) && m.timer >= 0 && m.timer < 1000
          && [m.nomonsters, m.respawn, m.fast].every((v) => v === 0 || v === 1);
        if (!ok) {
          l.send({ t: 'desync', why: `player ${m.you} has another WAD loaded` });
          netPanel(`The host is playing ${String(m.wad).split('|')[0]}: load that, and join again.`);
          return;
        }
        lobby.guest = null;
        lobby.role = null;
        $('net-mode').value = String(m.dm);
        $('net-timer').value = String(m.timer);
        for (const o of NET_OPTIONS) $(`net-${o}`).checked = m[o] === 1;
        beginNet({ me: m.you, players: m.players, mapName: m.map, skill: m.skill, seed: m.seed, deathmatch: m.dm, timer: m.timer,
          nomonsters: m.nomonsters, respawn: m.respawn, fast: m.fast, save }, new Map([[1, l]]))
          .catch((err) => setStatus(err.message, true));
      };
      l.onclose = () => { if (!net) { lobby.guest = null; lobby.role = null; netPanel('The host went away.'); } };
    }
  } catch (err) {
    netPanel(`That didn't work: ${err.message}`);
  }
});
// the launch options (-nomonsters, -respawn, -fast): a checkbox each, the host's choice for all
const NET_OPTIONS = ['nomonsters', 'respawn', 'fast'];
// the most a save may take in the start message (a data channel message tops out near 256 KB)
const NET_SAVE_MAX = 200000;
$('net-start').addEventListener('click', async () => {
  if (lobby.role !== 'host' || !lobby.links.length || !db || !wad) return;
  const players = lobby.links.length + 1;
  let mapName = $('map').value;
  let skill = settings.skill;
  const seed = 0;   // (G_InitNew's M_ClearRandom: every game starts at the table's beginning)
  let dm = Number($('net-mode').value) || 0;
  let timer = Math.max(0, Math.min(999, Number($('net-timer').value) || 0));
  let opts = Object.fromEntries(NET_OPTIONS.map((o) => [o, $(`net-${o}`).checked ? 1 : 0]));
  // -loadgame: the game, its map, skill and rules all come from the save
  const slot = Number($('net-save').value);
  let save = null;
  let packed;
  if (slot >= 0) {
    const rec = await saves.get(`${wadKey}|${slot}`).catch(() => null);
    if (!rec?.save) { netPanel('That save is gone.'); return; }
    if (savePlayers(rec.save) !== players) {
      netPanel(`That save is for ${savePlayers(rec.save)} player${savePlayers(rec.save) === 1 ? '' : 's'}, and ${players} are here.`);
      return;
    }
    packed = await packSave(rec.save);
    if (packed.length > NET_SAVE_MAX) { netPanel("That save is too big to send."); return; }
    save = await unpackSave(packed);   // (the host plays exactly what the others unpack)
    ({ map: mapName, skill } = save);
    dm = saveSetting(save, 'deathmatch');
    timer = saveSetting(save, 'time_limit');
    opts = Object.fromEntries(NET_OPTIONS.map((o) => [o, saveSetting(save, o) ? 1 : 0]));
    $('net-mode').value = String(dm);
    $('net-timer').value = String(timer);
    for (const o of NET_OPTIONS) $(`net-${o}`).checked = opts[o] === 1;
  }
  if (lobby.role !== 'host' || !lobby.links.length) return;   // (someone left while the save was packed)
  const links = new Map(lobby.links.map(({ pid, link }) => [pid, link]));
  lobby.invite?.cancel();
  Object.assign(lobby, { role: null, links: [], invite: null, guest: null });
  for (const [pid, link] of links) link.send({ t: 'start', you: pid, players, map: mapName, skill, seed, wad: wadKey, dm, timer, ...opts, ...(packed ? { save: packed } : {}) });
  beginNet({ me: 1, players, mapName, skill, seed, deathmatch: dm, timer, ...opts, save }, links).catch((err) => setStatus(err.message, true));
});
$('net-leave').addEventListener('click', () => {
  const was = !!net;
  leaveNet('you left the game');
  netPanel(was ? 'You left the game.' : '');
  $('net-out').value = '';
});
netPanel();

// ── the loop ─────────────────────────────────────────────────────────────
// requestAnimationFrame, with a timer as backstop: rAF stalls in occluded
// panes and iframes even while the page counts as visible.
function nextFrame() {
  let done = false;
  const go = () => {
    if (!done) {
      done = true;
      frame();
    }
  };
  requestAnimationFrame(go);
  setTimeout(go, 50);
}

let frames = 0;         // (frame() calls, for the tests' diagnostics)
let inFrame = null;     // the frame in flight (a map load waits for it: its queries must not meet the DELETEs)
let frameStarting = false;   // …unless that frame is itself waiting on a map start (its queries are done)
let loopAlive = false;  // frame() keeps scheduling itself until an error stops it; a map start revives it
function frame() {
  inFrame = frameNow().finally(() => { inFrame = null; });
}

/** running again, and the loop with it if an error had stopped it */
function resume() {
  lastTic = performance.now();
  running = true;
  if (!loopAlive) {
    loopAlive = true;
    nextFrame();
  }
}

/** the browser's gamepads (none where there's no Gamepad API) */
function gamepads() {
  try { return navigator.getGamepads?.() ?? []; } catch { return []; }
}

const joyMenu = new JoyMenu();
let joyButtonsHeld = false;
/**
 * The joystick outside play: M_Responder steps through the menus with it
 * (joywait), and on the title G_Responder opens the menu when a button goes down.
 */
function joystickScreens() {
  const js = readJoystick(gamepads());
  if (!js) return;
  const now = performance.now();
  const down = js.buttons.some(Boolean);
  if (menu?.active) {
    const k = joyMenu.key(js, now);
    if (k) menu.key(k);
  } else if (title && menu && down && !joyButtonsHeld) {
    openMenu();
    joyMenu.holdOff(now);
  }
  joyButtonsHeld = down;
}

async function frameNow() {
  frames++;
  joystickScreens();
  if (!running || document.hidden || (paused && !menu?.active)) {
    lastTic = performance.now();
    nextFrame();
    return;
  }
  try {
    const now = performance.now();
    const tics = Math.max(1, Math.min(6, Math.round((now - lastTic) / TIC_MS)));
    lastTic += tics * TIC_MS;
    if (now - lastTic > 200) lastTic = now;

    // the melt runs on its own, and nothing else does meanwhile (D_Display's wipe loop)
    if (melt) {
      readInput(tics);                // (what's pressed meanwhile is dropped)
      const done = melt.tick(tics);
      renderer.presenter?.present(melt.draw(meltBuf), meltPalette);
      if (done) melt = null;
      nextFrame();
      return;
    }

    if (title?.demo && !attract) {
      // D_DoAdvanceDemo: a demo's turn in the title loop
      await startAttract(title.demo);
      nextFrame();
      return;
    }

    if ((title && !attract) || (menu?.active && !net && !attract)) {
      // the title loop, or the menu over a frozen game (single player waits;
      // a netgame, or the title loop's demo, goes on beneath it)
      for (let i = 0; i < tics; i++) { title?.tick(); menu?.tick(); }
      if (title) title.draw(renderer);
      else if (menuBackdrop) renderer.sfb.set(menuBackdrop);
      if (menu?.active) menu.draw(renderer);
      renderer.present(title ? 0 : lastPalette);
      nextFrame();
      return;
    }

    if (intermission) {
      // WI_Ticker: a new press of fire or use hurries it along (a demo's own presses, playing one)
      const c = demoTake('wi');
      const input = readInput(tics);
      const n = c ? c[1] : tics;
      const buttons = c ? c[2] === 1 : input[4] === 1 || input[5] === 1;
      recorder?.push(['wi', n, buttons ? 1 : 0]);
      // (a .lmp says the press itself: WI_checkForAccelerate, fire and use apart)
      const pressed = c && c.length > 3 ? c[3] === 1 : buttons && !wiButtons;
      for (let i = 0; i < n; i++) intermission.tick(i === 0 && pressed);
      wiButtons = buttons;
      if (intermission.done) {
        // G_WorldDone: a text screen if this exit has one, else the next map
        const { secret, fromName } = intermission;
        intermission = null;
        if (Finale.available(wad, fromName, secret)) {
          finale = new Finale(renderer, audio, wad, res.dehacked, fromName, secret);
          finaleKey = false;
        } else {
          await nextLevel(nextMap(fromName, secret, wad.mapNames()), false);
        }
      } else intermission.draw();
      nextFrame();
      return;
    }

    if (finale) {
      // the ending runs on its own clock: a key kills the one on stage (the
      // key that skips the text is spent before the cast starts), fire or
      // use held skips the text
      const c = demoTake('fin');
      const input = readInput(tics);
      const n = c ? c[1] : tics;
      const held = c ? c[2] === 1 : input[4] === 1 || input[5] === 1;
      const pressed = c ? c[3] === 1 : finaleKey;
      finaleKey = false;
      recorder?.push(['fin', n, held ? 1 : 0, pressed ? 1 : 0]);
      if (pressed) finale.press();
      for (let i = 0; i < n; i++) finale.tick(held);
      if (finale.done) {
        // G_WorldDone after a text screen: on to the next map (MAP31/32 after a
        // secret exit's), inventory kept. DOOM I's endings never get here: the
        // game is over, and their art stays until the menu starts another
        await nextLevel(nextMap(finale.from, finale.secret, wad.mapNames()), false);
        nextFrame();
        return;
      }
      finale.draw();
      nextFrame();
      return;
    }

    let t = performance.now();
    if (net) {
      // a netgame: this player's commands out, everyone's tics in (TryRunTics)
      const done = await netTics(tics);
      if (!done) { nextFrame(); return; }
    } else {
      // G_ReadDemoTiccmd / G_WriteDemoTiccmd
      let args;
      if (demoPlayer) {
        readInput(tics);              // (drained, so it doesn't pile up for later)
        // a demo plays in its own time: as many of its calls as the tics gone
        // by cover (at least one), whatever the frame rate it was recorded at
        // (-timedemo: one call a frame, whatever the clock says, as G_TimeDemo's singletics)
        const timing = timedemo && timedemo.ms == null;
        if (timing && timedemo.start == null) timedemo.start = performance.now();   // (from the first tic: the load's melt isn't counted)
        let owed = timing ? 1 : tics;
        let ran = 0;
        while (owed > 0) {
          args = demoTake('tic');
          if (!args) break;
          if (args[0] === 'net') {
            // a netgame's .lmp: every player's ticcmd, one NET_TIC (TryRunTics with them all in hand)
            await db.execBatch('INSERT INTO ticcmd (player_id, fwd, side, turn, fire, use_key, weapon_sel, run) VALUES (?, ?, ?, ?, ?, ?, ?, ?)', args[1]);
            hud = (await db.query('SELECT * FROM net_tic', [], { rowMode: 'object' })).rows[0];
            await db.exec('DELETE FROM ticcmd');
            args = [1, ...args[1].find((c) => c[0] === demoNet?.me).slice(1)];
          } else hud = (await db.query('SELECT * FROM doom_tic(?, ?, ?, ?, ?, ?, ?, ?)', args, { rowMode: 'object' })).rows[0];
          owed -= timing ? owed : args[0];
          if (timing) { timedemo.frames++; timedemo.tics += args[0]; }
          ran++;
          lastFire = args[4] === 1;
          if (hud.EXIT_KIND) break;
        }
        if (!ran) { nextFrame(); return; }
      } else {
        args = readInput(tics);
        lastFire = args[4] === 1;
        recorder?.push(args);
        if (recorder && recorder.demo.calls.length % 35 === 0) setStatus(`● Recording a demo of ${map.name}: ${recorder.tics} tics`);
        hud = (await db.query('SELECT * FROM doom_tic(?, ?, ?, ?, ?, ?, ?, ?)', args, { rowMode: 'object' })).rows[0];
      }
    }
    lastFrame.tic = performance.now() - t;

    if (hud.EXIT_KIND) {
      const kind = hud.EXIT_KIND;
      const secret = kind === 2;
      // (a demo goes on: through the intermission to the next level, or the restart after a death)
      if (kind === 3) await nextLevel(map.name, true, true);
      else {
        await db.exec('UPDATE game SET exit_kind = 0 WHERE id = 1');
        const level = levelOf(map.name);
        if (!level.doom2 && level.map === 7 && Finale.available(wad, map.name, secret)) {
          // G_DoCompleted: DOOM I's E?M8 goes straight to the ending, no stats
          finale = new Finale(renderer, audio, wad, res.dehacked, map.name, secret);
          finaleKey = false;
          setStatus(`The end of episode ${level.episode}. Esc for the menu: a new game, or another episode.`);
        } else {
          // WI_Start: kills, items, secrets, time and par; then G_WorldDone
          if (!level.doom2 && level.map === 8) didSecret.add(level.episode);   // E?M9 done
          intermission = new Intermission(renderer, audio, wad, map.name, nextMap(map.name, secret, wad.mapNames()), {
            kills: hud.KILLS, totalKills: hud.TOTAL_KILLS, items: hud.ITEMS, totalItems: hud.TOTAL_ITEMS,
            secrets: hud.SECRETS, totalSecrets: hud.TOTAL_SECRETS, time: hud.TIC,
            ...(net ? await netStats() : {}),
          }, didSecret.has(level.episode));
          intermission.secret = secret;
          wiButtons = true;   // (the button that pulled the switch doesn't count)
          if (demoPlayer?.lmp) {
            const p = (await db.query('SELECT p.attack_down, p.use_down FROM player p WHERE p.id = 1')).rows[0];
            demoPlayer.carry(p.ATTACK_DOWN === 1, p.USE_DOWN === 1);
          }
        }
      }
      nextFrame();
      return;
    }
    if (hud.SIDES_REV !== sidesRev) {
      if (sidesRev !== -1) await loadSides();
      sidesRev = hud.SIDES_REV;
    }

    // queued together so the Worker never sits idle between them
    t = performance.now();
    const arr = { rowMode: 'array' };
    const q = (sql) => db.query(sql, [], arr).then((r) => {
      const ms = performance.now() - t;
      t = performance.now();
      return [r.rows, ms];
    });
    // FRAME_WALLS depends on the view (where you stand and look, the view's
    // size, the renderer) and on the sectors' heights; the rest of what it
    // reads is the map's own. While none of that changes (standing still, no
    // door or lift moving) its answer is the same, so the last one is kept.
    const [sectors] = await q('SELECT * FROM frame_sectors');
    const wallKey = [levelSerial, settings.renderer, renderer.w, renderer.h, viewer(), hud.PX, hud.PY, hud.PANGLE, hud.VIEW_Z,
      sectors.map((r) => `${r[1]},${r[2]},${r[6]}`).join(';')].join('|');
    const reuse = wallCache?.key === wallKey;
    if (reuse) wallsReused++;
    t = performance.now();
    const [[walls, wallMs], [sprites, spriteMs], [sounds]] = await Promise.all([
      reuse ? [wallCache.walls, 0] : q('SELECT * FROM frame_walls'), q('SELECT * FROM frame_sprites'),
      q(`SELECT id, sound, origin, x, y FROM sound_events WHERE id > ${lastSoundId} AND COALESCE(listener, ${viewer()}) = ${viewer()} ORDER BY id`),
    ]);
    // the listener; on map 8 (E?M8, MAP08) S_AdjustSoundParams never quite fades a sound out
    const listener = { x: hud.PX, y: hud.PY, angle: hud.PANGLE, bossMap: /^(E\dM8|MAP08)$/.test(map.name) };
    if (sounds.length) {
      lastSoundId = sounds[sounds.length - 1][0];
      audio.playEvents(sounds, listener);
    }
    // S_UpdateSounds: the things sounding now, where they are (gone: their sound stops)
    const sounding = audio.channels.thingOrigins();
    const positions = new Map(sounding.length
      ? (await db.query(`SELECT id, x, y FROM things WHERE id IN (${sounding.join(', ')})`, [], arr)).rows.map((r) => [r[0], [r[1], r[2]]])
      : []);
    audio.update(listener, positions);
    for (const r of walls) map.seen.add(r[3]);
    // AM_drawThings (IDDT twice): where everything is
    map.amThings = showMap && amCheating === 2
      ? (await db.query("SELECT x, y, angle FROM things WHERE kind NOT IN ('player', 'marker')", [], arr)).rows
      : null;
    // AM_drawPlayers in a netgame: where everyone is
    map.amPlayers = showMap && (net || demoNet)
      ? (await db.query('SELECT p.id, t.x, t.y, t.angle, p.invis_tics invis FROM player p JOIN things t ON t.id = p.thing_id ORDER BY p.id',
        [], { rowMode: 'object' })).rows.map((r) => ({ id: r.ID, x: r.X, y: r.Y, angle: r.ANGLE, invis: r.INVIS }))
      : null;
    wallCache = { key: wallKey, walls };
    lastFrame.walls = wallMs;
    lastFrame.sprites = spriteMs;
    lastFrame.rows = walls.length;

    t = performance.now();
    map.sectors = new Map(sectors.map((r) => [r[0], { floor: r[1], ceil: r[2], floorFlat: r[3], ceilFlat: r[4], light: r[5], sky: r[6] === 1 }]));
    // P_PlayerThink's fixedcolormap: invulnerable, the inverse greys (32);
    // with the goggles, nearly full bright (1) – both flicker off in the last four seconds
    const blink = (n) => n > 4 * 32 || (n & 8);
    const fixedColormap = blink(hud.INVULN_TICS) ? 32 : blink(hud.INFRA_TICS) ? 1 : null;
    // ST_doPaletteStuff: pain red (berserk's red, fading over 768 tics, counts
    // as pain), else pickup gold, else the radiation suit's green (13)
    const red = Math.max(hud.DAMAGE_COUNT, hud.STRENGTH_TICS ? 12 - (hud.STRENGTH_TICS >> 6) : 0);
    const palette = red > 0 ? Math.min(8, (red + 7) >> 3)
      : hud.BONUS_COUNT ? Math.min(12, 8 + ((hud.BONUS_COUNT + 7) >> 3))
        : blink(hud.IRON_TICS) ? 13 : 0;
    renderer.drawView({ x: hud.PX, y: hud.PY, z: hud.VIEW_Z, angle: hud.PANGLE, tic: hud.TIC, palette, fixedColormap,
      extralight: extraLight(hud) },
      walls, sprites, map);
    // R_DrawViewBorder round a small view (FLOOR7_2, DOOM II's GRNROCK)
    if (renderer.scaledW < 320) renderer.drawBorder(wad.mapNames().some((m) => m.startsWith('MAP')) ? 'GRNROCK' : 'FLOOR7_2');
    renderer.composeView();
    drawWeapon(renderer, hud);   // (dead, it's lowered out of sight: A_Lower)
    if (showMap) drawAutomap(tics);
    // (the face still keeps its time with the bar off: ST_Ticker runs regardless)
    const faceNow = face.update(hud, tics, lastFire);
    // ST_Drawer: full screen (11) has no status bar, except over the automap
    if (settings.screenSize < 11 || showMap) drawStatusBar(renderer, hud, faceNow, net?.me ?? demoNet?.me ?? 0);
    if (amMsg) amMsg.tics -= tics;
    if (amMsg && amMsg.tics <= 0) amMsg = null;
    const msg = amMsg?.text ?? hud.MSG;
    if (msg && (settings.messages || amMsg?.always)) drawText(renderer, msg, 2, 2);
    // (w_chat: HU_INPUTY, a line of the font below the message)
    const typing = net?.chat?.shown;
    if (typing) drawText(renderer, typing, 2, 2 + (renderer.pictureByName('STCFN065')?.h ?? 7) + 1);
    if (paused || net?.paused) drawText(renderer, 'PAUSED', 136, 80);
    if ((net || attract) && menu?.active) {
      for (let i = 0; i < tics; i++) menu.tick();
      menu.draw(renderer);
    }
    lastPalette = palette;
    renderer.present(palette);
    lastFrame.draw = performance.now() - t;
    updateStats();
  } catch (err) {
    console.error(err);
    setStatus(`Error: ${err.message}`, true);
    running = false;
    loopAlive = false;
    return;
  }
  nextFrame();
}


let fpsT = performance.now();
let fpsN = 0;
let fps = 0;
function updateStats() {
  fpsN++;
  const now = performance.now();
  if (now - fpsT > 500) {
    fps = (fpsN * 1000) / (now - fpsT);
    fpsT = now;
    fpsN = 0;
  }
  statsEl.textContent =
    `${fps.toFixed(1)} fps · ${settings.renderer === 'bsp' ? 'BSP' : 'brute'} · doom_tic ${lastFrame.tic.toFixed(0)} ms · frame_walls ${lastFrame.walls.toFixed(0)} ms ` +
    `(${lastFrame.rows} slices, ${renderer.visplaneCount ?? 0} visplanes) · frame_sprites ${lastFrame.sprites.toFixed(0)} ms · raster ${lastFrame.draw.toFixed(0)} ms` +
    ` · ${presenter?.kind === 'webgl' ? 'WebGL' : '2D'}${settings.smooth ? ' smooth' : ''}`;
}

/** An automap message (a DEHACKED string's name), the WAD's wording or Freedoom's. */
function amSay(key) {
  const strings = parseDehStrings(wad.dehacked() + '\n' + (dehPatch?.text ?? ''));
  amMsg = { text: strings.get(key) ?? AM_STRINGS[key], tics: 4 * 35 };   // (HU_MSGTIMEOUT)
}

/** A message of the page's own (F5, F8, F11), like the game's; ALWAYS shows it even with messages off (message_dontfuckwithme) */
/**
 * A chat line: your own as a message like any other; another player's for
 * you stays up (message_nottobefuckedwith), shows with messages off, and
 * beeps: DSRADIO in DOOM II, DSTINK in DOOM.
 */
function chatSay(text, forMe = false) {
  amMsg = { text, tics: HU_MSGTIMEOUT, always: forMe };
  if (forMe) {
    const lump = wad.mapNames().some((m) => m.startsWith('MAP')) ? 'DSRADIO' : 'DSTINK';
    audio.playEvents([[0, lump, 'menu', null, null]], { x: 0, y: 0, angle: 0 });
  }
}

function pageSay(text, always = false) {
  amMsg = { text, tics: 4 * 35, always };
  lastSaid = text;
}

/** F11 (and the setting): usegamma, the presenter's palettes through the gamma table, with its message */
function setGamma(level, say = false) {
  settings.gamma = Math.max(0, Math.min(4, level | 0));
  saveSettings();
  renderer?.setGamma(settings.gamma);
  if (say) {
    const strings = parseDehStrings(wad.dehacked() + '\n' + (dehPatch?.text ?? ''));
    pageSay(strings.get(`GAMMALVL${settings.gamma}`) ?? (settings.gamma ? `Gamma: level ${settings.gamma}` : 'Gamma: off'));
  }
}

function drawAutomap(tics = 1) {
  // AM_Ticker, then AM_Drawer into the 320×200 screen in palette colours;
  // the view behind stays faintly visible, darkened through COLORMAP 24
  const k = (c) => keys.has(c);
  amView.tick(tics, { zoomIn: k('Equal') || k('NumpadAdd'), zoomOut: k('Minus') || k('NumpadSubtract'),
    left: k('ArrowLeft'), right: k('ArrowRight'), up: k('ArrowUp'), down: k('ArrowDown') }, { x: hud.PX, y: hud.PY });
  renderer.dim(24);
  const tx = (x) => amView.toScreen(x, 0)[0];
  const ty = (y) => amView.toScreen(0, y)[1];
  const sc = amView.scale;
  // AM_drawGrid: the BLOCKMAP's 128-unit cells, under everything
  if (amView.grid) for (const [x1, y1, x2, y2] of amView.gridLines()) renderer.line(tx(x1), ty(y1), tx(x2), ty(y2), GRID_COLOR);
  for (const r of map.linedefs) {
    const [id, , bs, flags, , x1, y1, x2, y2, fsec, bsec, special] = r;
    const f = map.sectors.get(fsec);
    const b = bs == null || bsec == null ? null : map.sectors.get(bsec);
    const color = automapColor({ flags, special }, f, b, map.seen.has(id), hud.ALLMAP === 1, amCheating);
    if (color == null) continue;
    renderer.line(tx(x1), ty(y1), tx(x2), ty(y2), color);
  }
  // a little triangle: the arrow for you, thintriangle_guy for things
  const tri = (px, py, ang, tip, back, color) => {
    const p = [[ang, tip], [ang + 2.5, back], [ang - 2.5, back]].map(([d, r]) => [px + Math.cos(d) * r, py - Math.sin(d) * r]);
    renderer.line(p[0][0], p[0][1], p[1][0], p[1][1], color);
    renderer.line(p[1][0], p[1][1], p[2][0], p[2][1], color);
    renderer.line(p[2][0], p[2][1], p[0][0], p[0][1], color);
  };
  // (the arrows keep their size on screen at any zoom, as DOOM's line art does: 16 and 8 units at the start)
  const z = Math.max(0.5, Math.min(2, sc / 0.12));
  if (map.amThings) {
    for (const [x, y, ang] of map.amThings) {
      const px = tx(x);
      const py = ty(y);
      if (px < -4 || px > 324 || py < -4 || py > 172) continue;
      tri(px, py, ang, 3 * z, 2.5 * z, AM_COLORS.thing);
    }
  }
  // AM_drawPlayers: your arrow in white; in a netgame everyone's in their colour (deathmatch: only yours)
  const me = viewer();
  const ng = net ?? demoNet;
  const arrows = automapPlayers(ng && map.amPlayers ? map.amPlayers : [{ id: me, x: hud.PX, y: hud.PY, angle: hud.PANGLE }],
    { me, netgame: !!ng, deathmatch: ng?.deathmatch ?? 0 });
  for (const a of arrows) tri(tx(a.x), ty(a.y), a.angle, 6 * z, 5 * z, a.color);
  amArrows = arrows.map((a) => ({ id: a.id, color: a.color }));
  // AM_drawMarks: the numbers, AMMNUM0–9
  amView.marks.forEach(([x, y], i) => {
    const pic = renderer.pictureByName(`AMMNUM${i}`);
    if (pic) renderer.patch(pic, Math.round(tx(x)), Math.round(ty(y)));
  });
}

// ── SQL console ─────────────────────────────────────────────────────────
async function runConsole() {
  const sqlText = $('sql').value.trim();
  if (!sqlText || !db) return;
  const out = $('sql-out');
  const t0 = performance.now();
  try {
    const r = /^\s*(select|with|execute\s+block)/i.test(sqlText)
      ? await db.query(sqlText, [], { rowMode: 'object' })
      : { rows: [], fields: [], exec: await db.exec(sqlText) };
    const ms = (performance.now() - t0).toFixed(1);
    if (!r.rows.length) {
      out.textContent = `OK (${ms} ms)`;
      return;
    }
    const cols = Object.keys(r.rows[0]);
    const lines = [cols.join('\t'), ...r.rows.slice(0, 200).map((row) => cols.map((c) => fmt(row[c])).join('\t'))];
    out.textContent = `${r.rows.length} row(s), ${ms} ms\n${lines.join('\n')}`;
  } catch (err) {
    out.textContent = err.message;
  }
}
const fmt = (v) => (typeof v === 'number' && !Number.isInteger(v) ? v.toFixed(2) : String(v));

$('run-sql').addEventListener('click', runConsole);
$('sql').addEventListener('keydown', (e) => {
  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) runConsole();
});
for (const b of document.querySelectorAll('[data-sql]')) {
  b.addEventListener('click', () => {
    $('sql').value = b.dataset.sql;
    runConsole();
  });
}

// ── boot ────────────────────────────────────────────────────────────────
async function openDatabase() {
  // The engine is firebird-wasm's build without threads, in a Worker: it needs no
  // SharedArrayBuffer, so no cross-origin isolation. The page once had a service
  // worker for that (coi-serviceworker.js); a browser that still has it lets it go.
  // Today's (sw.js) is for offline play.
  navigator.serviceWorker?.getRegistrations?.()
    .then((regs) => regs.filter((r) => /coi-serviceworker/.test(r.active?.scriptURL ?? '')).forEach((r) => r.unregister()))
    .then(() => navigator.serviceWorker.register('./sw.js'))
    .catch((err) => console.warn('[firebird-doom] no offline play:', err.message));
  setStatus('Starting Firebird 6 (WebAssembly)…');
  const instance = new FirebirdBrowser('memory://doom', {
    worker: new Worker(new URL('./firebird-engine-worker.js', import.meta.url)),
    multiTab: 'allow-unsafe',
    autoPersist: false,
  });
  const v = await instance.query("SELECT rdb$get_context('SYSTEM', 'ENGINE_VERSION') AS v FROM rdb$database");
  $('engine').textContent = `Firebird ${v.rows[0].V}`;
  setStatus('Creating the DOOM schema…');
  await createSchema(instance, { schema: schemaSql, game: gameSql, render: renderSql });
  return instance;
}

// what's loaded: the main WAD, PWADs on top of it (-file), and a DeHackEd patch (-deh)
let baseWad = null;                 // { buffer, label }
let pwads = [];                     // [{ buffer, name }]
let dehPatch = null;                // { text, name }

/** Load the main WAD, with the PWADs and patch last loaded over it (remembered in IndexedDB). */
async function useWad(buffer, label) {
  baseWad = { buffer, label };
  const kept = await recallFiles(saves, label);
  pwads = kept?.pwads ?? [];
  dehPatch = kept?.deh ?? null;
  if (!kept) { await loadWads(); return; }
  try {
    await loadWads();
  } catch (err) {
    // (they no longer load: forget them, and start on the main WAD alone)
    pwads = [];
    dehPatch = null;
    await rememberFiles(saves, label, [], null);
    await loadWads();
    setStatus(`The PWADs kept for ${label} didn't load (${err.message}); they're forgotten.`, true);
  }
}

/** Remember what's over the main WAD now for next time (first: loading takes a while), and load it. */
async function reloadWads() {
  await rememberFiles(saves, baseWad.label, pwads, dehPatch).catch(() => {});
  await loadWads();
}
const forgetFailed = () => rememberFiles(saves, baseWad.label, pwads, dehPatch).catch(() => {});

/** W_InitMultipleFiles: the main WAD, the PWADs over it, the patch over all; then the first map. */
async function loadWads() {
  running = false;
  wad = new Wad(baseWad.buffer, ...pwads.map((p) => p.buffer));
  const label = [baseWad.label, ...pwads.map((p) => p.name), ...(dehPatch ? [dehPatch.name] : [])].join(' + ');
  const maps = wad.mapNames();
  if (!maps.length) throw new Error(`${label} has no maps`);
  setStatus(`Copying ${label} resources into Firebird…`);
  res = await loadResources(db, wad, { width: view().w, height: view().h, projy: view().scaledW / 2, dehacked: dehPatch?.text ?? '' });
  await setRenderer(db, settings.renderer === 'bsp');
  renderer = new Renderer(wad, res);
  renderer.setGamma(settings.gamma);
  renderer.attach(presenter);
  // every present goes through here: a new state melts in from the last screen shown
  const present = renderer.present.bind(renderer);
  shownState = null;
  renderer.present = (palette = 0) => {
    const state = screenState();
    if (shownState !== null && state !== shownState && shownScreen) {
      melt = new Melt(shownScreen, renderer.sfb);   // wipe_StartScreen, wipe_EndScreen
      meltPalette = palette;
      renderer.presenter?.present(melt.draw(meltBuf), palette);
    } else present(palette);
    shownState = state;
    shownScreen = renderer.sfb.slice();
  };
  audio.setWad(wad);
  // DeHackEd: the WAD's patch respells cheats and sets par times (the rest went into Firebird)
  cheats = cheatReaders(res.dehacked.cheats);
  setParOverrides(res.dehacked.pars);
  if (res.dehacked.report.length) console.info(`DEHACKED (${label}): ${res.dehacked.report.join('; ')}`);
  wadKey = `${label}|${maps.length}`;
  // the title loop's demos (only the bundled WADs on their own have any)
  attractDemos = new Map();
  if (!pwads.length && !dehPatch) {
    const key = wadKey;
    for (let n = 1; n <= (ATTRACT_DEMOS[baseWad.label] ?? 0); n++) {
      fetch(new URL(`./demos/${attractDemoFile(baseWad.label, n)}`, location.href))
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (d && key === wadKey && !demoProblem(d) && d.wad === key) attractDemos.set(n, d); })
        .catch(() => {});
    }
  }
  saveSlots = Array(SLOTS).fill(null);
  refreshSlots();
  lastDemo = null;
  saves.get(`${wadKey}|demo`).then((d) => { if (d && !demoProblem(d)) lastDemo = d; updateDemoButtons(); }).catch(() => {});
  makeMenu();
  renderer.setSize(view().w, view().h, view());
  const sel = $('map');
  sel.innerHTML = maps.map((m) => `<option>${m}</option>`).join('');
  $('wadname').textContent = label;
  $('pwad-clear').disabled = !pwads.length && !dehPatch;
  // (with a PWAD, start on its first map – vanilla would need -warp for that)
  const first = wad.pwadMapNames()[0] ?? maps[0];
  sel.value = first;
  await startMap(first, true);
}

/** Fetch one of the bundled Freedoom IWADs and load it into Firebird. */
async function loadGame(game) {
  const file = game === 'freedoom2' ? 'freedoom2.wad' : 'freedoom1.wad';
  setStatus(`Downloading ${file}…`);
  const url = new URL(`./wads/${file}`, location.href);
  const resp = await fetch(url).catch(() => null);
  if (!resp?.ok) {
    // (sw.js answers 504 with x-offline when there's no network and no copy kept)
    const why = !resp ? 'no answer' : resp.headers.get('x-offline') ? 'offline, and not played here before' : resp.status;
    throw new Error(`could not fetch ${file} (${why}); pick a WAD file instead`);
  }
  keepWad(url, resp.clone());
  await useWad(await resp.arrayBuffer(), file);
}

// Offline play: a WAD the page downloaded goes into the service worker's cache
// (sw.js), as it came, unless that copy is already there: it isn't fetched twice
const WAD_CACHE = 'firebird-doom-wads';
async function keepWad(url, resp) {
  try {
    if (!('caches' in window)) return;
    const c = await caches.open(WAD_CACHE);
    const had = await c.match(url);
    const same = (h) => ['etag', 'last-modified', 'content-length'].every((k) => had.headers.get(k) === resp.headers.get(k));
    if (had && same()) return;
    await c.put(url, resp);
  } catch (err) { console.warn('[firebird-doom] the WAD stays online only:', err.message); }
}

/** What there is to play offline: the service worker in charge, its version, the WADs kept */
async function offlineState() {
  const sw = navigator.serviceWorker;
  const wads = 'caches' in window ? (await (await caches.open(WAD_CACHE)).keys()).map((r) => r.url.split('/').pop()) : [];
  const pages = 'caches' in window ? (await caches.keys()).filter((k) => k.startsWith('firebird-doom-page-')) : [];
  return { controlled: !!sw?.controller, page: pages.map((k) => k.slice('firebird-doom-page-'.length)), wads };
}

async function boot() {
  try {
    db = await openDatabase();
    // DOOM II's story text for WADs without it (id's doom2.wad): Freedoom's, built at deploy
    fetch(new URL('./wads/freedoom-strings.json', location.href))
      .then((r) => (r.ok ? r.json() : null)).then((d) => d && setFallbackStrings(d)).catch(() => {});
    // for the devtools console: await doom.sql('SELECT * FROM player')
    window.doom = {
      db, audio, sql: (q, p) => db.query(q, p).then((r) => r.rows),
      offline: offlineState,
      get renderer() { return renderer; }, get presenter() { return presenter; },
      // previews for testing a WAD's screens without playing to them (try id's
      // doom.wad: doom.finale('E3M8') is the bunny); afterwards the game goes on
      // to the map after the one named, as if you'd just finished it (DOOM I's
      // endings excepted: they end the game)
      get menu() { return menu; },
      get automap() { return { view: amView, open: showMap, message: amMsg?.text ?? null, arrows: amArrows }; },
      get gamma() { return settings.gamma; },
      get options() { return { checked: { ...launchOptions }, game: { ...gameOptions } }; },
      get keys() { return JSON.parse(JSON.stringify(bindings)); },
      get timedemo() { return timedemo && { ...timedemo }; },
      get commandLine() { return commandLine; },
      // the mouse, for tests: DOM-style moves and buttons, as under pointer lock
      mouse: { move: (dx, dy) => mouse.move(dx, dy, settings.mouse), button: (b, down) => mouse.button(b, down) },
      get debug() { return { running, paused, frames, wallsReused, loopAlive, levelSerial, shownState, menu: !!menu?.active, melt: !!melt, keys: [...keys], mouseTurn, showMap, amFollow: amView?.follow, ls: net && { submitted: net.ls.submitted, executed: net.ls.executed, ready: net.ls.ready.length, early: net.ls.early.size, pending: net.ls.pending.size, error: net.ls.error } }; },
      get message() { return amMsg?.text ?? hud?.MSG ?? null; },
      get lastSaid() { return lastSaid; },
      get files() { return { base: baseWad?.label, pwads: pwads.map((p) => p.name), deh: dehPatch?.name ?? null }; },
      addPwad: async (buffer, name) => { pwads.push({ buffer, name }); await reloadWads(); },
      useDeh: async (text, name = 'patch.deh') => { dehPatch = { text, name }; await reloadWads(); },
      get demo() { return { recording: !!recorder, playing: !!demoPlayer && !attract, attract, lmp: !!demoPlayer?.lmp, lmpEnd, played: demoPlayer?.index ?? 0, last: lastDemo }; },
      // a .lmp (its bytes), as the Load… button plays one
      playLmp: (bytes, name) => playLmp(Uint8Array.from(bytes), name),
      get chat() { return net && { on: net.chat.on, line: net.chat.line.text, queued: net.chat.queue.length }; },
      get net() { return net && { me: net.me, players: net.players, deathmatch: net.deathmatch, timer: net.timer, nomonsters: net.nomonsters, respawn: net.respawn, fast: net.fast, tic: net.ls.executed, error: net.ls.error, sums: Object.fromEntries(net.sums), paused: net.paused }; },
      // a slot's save, for tests: its name, map and the game it holds
      saveRecord: (slot) => saves.get(`${wadKey}|${slot}`),
      // the walls the page drew last, against FRAME_WALLS asked now (the kept answer must be the same)
      wallsMatch: async () => JSON.stringify(wallCache?.walls) === JSON.stringify((await db.query('SELECT * FROM frame_walls', [], { rowMode: 'array' })).rows),
      record: () => startRecording(), stopDemo: () => $('demo-stop').click(), playDemo: (d = lastDemo) => playDemo(d),
      get title() { return title; },
      get melting() { return !!melt; },
      // the screen shown is the current one, no melt pending or running (melts queue up: a
      // level loaded behind another's melt gets its own after)
      get settled() { return !melt && shownState === screenState(); },
      get screen() { return screenState().replace(/ \d+$/, ''); },   // title, level, intermission, finale
      finale(name, secret = false) {
        if (!Finale.available(wad, name, secret)) return `no screen after ${name}${secret ? "'s secret exit" : ''} in this WAD`;
        intermission = null;
        title = null;
        menu?.close(true);
        finale = new Finale(renderer, audio, wad, res.dehacked, name, secret);
        finaleKey = false;
        return `showing the screen after ${name}`;
      },
      intermission(from, to = nextMap(from, false, wad.mapNames()), stats = {}) {
        finale = null;
        title = null;
        menu?.close(true);
        intermission = new Intermission(renderer, audio, wad, from, to, {
          kills: 17, totalKills: 20, items: 30, totalItems: 37, secrets: 2, totalSecrets: 3, time: 95 * 35, ...stats,
        }, didSecret.has(levelOf(from).episode));
        intermission.secret = false;
        wiButtons = true;
        return `showing the intermission ${from} → ${to}`;
      },
    };
    // (-iwad: one of the bundled WADs for this visit, the Game setting left as it is)
    if (commandLine.iwad) $('game').value = commandLine.iwad;
    await loadGame(commandLine.iwad ?? settings.game);
    goTitle();   // DOOM starts on its title screen; a key brings up the menu
    resume();    // (the loop is running since the first map; this would start it otherwise)
    await runCommandLine();
  } catch (err) {
    console.error(err);
    setStatus(err.message, true);
  }
}

$('wadfile').addEventListener('change', async (e) => {
  const f = e.target.files[0];
  if (!f || !db) return;
  try {
    await useWad(await f.arrayBuffer(), f.name);
  } catch (err) {
    setStatus(err.message, true);
  }
});
// PWADs on top of the main WAD, and a DeHackEd patch
$('pwadfile').addEventListener('change', async (e) => {
  const files = [...e.target.files];
  if (!files.length || !db || !baseWad) return;
  try {
    for (const f of files) pwads.push({ buffer: await f.arrayBuffer(), name: f.name });
    await reloadWads();
  } catch (err) {
    pwads = pwads.filter((p) => !files.some((f) => f.name === p.name));
    forgetFailed();
    setStatus(err.message, true);
  }
  e.target.value = '';
});
$('dehfile').addEventListener('change', async (e) => {
  const f = e.target.files[0];
  if (!f || !db || !baseWad) return;
  try {
    dehPatch = { text: new TextDecoder('latin1').decode(await f.arrayBuffer()), name: f.name };
    await reloadWads();
  } catch (err) {
    dehPatch = null;
    forgetFailed();
    setStatus(err.message, true);
  }
  e.target.value = '';
});
$('pwad-clear').addEventListener('click', async () => {
  pwads = [];
  dehPatch = null;
  try { await reloadWads(); } catch (err) { setStatus(err.message, true); }
});
$('game').value = settings.game;
$('game').addEventListener('change', async (e) => {
  settings.game = e.target.value;
  saveSettings();
  if (!db) return;
  try {
    await loadGame(settings.game);
  } catch (err) {
    setStatus(err.message, true);
  }
});
$('map').addEventListener('change', (e) => startMap(e.target.value, true).catch((err) => setStatus(err.message, true)));
$('detail').value = settings.detail;
$('renderer').value = settings.renderer;
function setAudio(on) {
  settings.audio = on;
  saveSettings();
  $('audio').checked = on;
  $('sfxvol').disabled = !on;
  $('musicvol').disabled = !on;
  $('synth').disabled = !on;
  audio.setEnabled(on);
}
$('audio').checked = settings.audio;
$('sfxvol').disabled = !settings.audio;
$('musicvol').disabled = !settings.audio;
$('synth').disabled = !settings.audio;
$('synth').value = settings.synth;
$('synth').addEventListener('change', (e) => {
  settings.synth = e.target.value;
  saveSettings();
  audio.unlock();
  audio.setOpl3(settings.synth === 'opl3');
});
$('audio').addEventListener('change', (e) => setAudio(e.target.checked));
$('sfxvol').value = settings.sfx;
$('musicvol').value = settings.music;
for (const id of ['sfxvol', 'musicvol']) {
  $(id).addEventListener('input', () => {
    settings.sfx = Number($('sfxvol').value);
    settings.music = Number($('musicvol').value);
    saveSettings();
    audio.unlock();
    applyVolumes();
  });
}
$('renderer').addEventListener('change', async (e) => {
  settings.renderer = e.target.value;
  saveSettings();
  if (db) await setRenderer(db, settings.renderer === 'bsp');
});
// the skill: a new game on the current map, as DOOM's New Game menu would
$('skill').value = String(settings.skill);
// ── the Keys panel: SETUP.EXE's key screen ─────────────────────────────────
let keyCapture = null;   // { action, slot } while a slot waits for its key
function keysPanel(say = '') {
  const list = $('keys-list');
  list.replaceChildren(...ACTIONS.map(([a, label, cfg]) => {
    const row = document.createElement('div');
    row.className = 'keys-row';
    const name = document.createElement('span');
    name.textContent = label;
    name.title = cfg;
    row.append(name, ...[0, 1].map((slot) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.id = `key-${a}-${slot}`;
      const waiting = keyCapture?.action === a && keyCapture.slot === slot;
      b.textContent = waiting ? 'press a key…' : keyName(bindings[a][slot]);
      b.className = waiting ? 'waiting' : '';
      b.addEventListener('click', () => { keyCapture = { action: a, slot }; keysPanel('Press the key (Esc: leave it, Backspace: none).'); });
      return b;
    }));
    return row;
  }));
  $('keys-status').textContent = say;
}
/** the key pressed for the slot waiting: Esc leaves it, Backspace clears it, a key the page keeps is refused */
function captureKey(e) {
  e.preventDefault();
  e.stopPropagation();
  const { action, slot } = keyCapture;
  keyCapture = null;
  if (e.code === 'Escape') { keysPanel(); return; }
  const code = e.code === 'Backspace' ? null : e.code;
  const why = code && reservedKey(code);
  if (why) { keysPanel(`${keyName(code)}: ${why}.`); return; }
  setBindings(bindKey(bindings, action, slot, code));
  keysPanel();
}
function setBindings(next) {
  bindings = next;
  settings.keys = bindings;
  saveSettings();
  updateGameKeys();
}
$('keys-defaults').addEventListener('click', () => { keyCapture = null; setBindings(keysFrom(null)); keysPanel('Back to the defaults.'); });
keysPanel();

$('skill').addEventListener('change', (e) => {
  settings.skill = Number(e.target.value);
  saveSettings();
  if (db && map) startMap(map.name, true).catch((err) => setStatus(err.message, true));
});
for (const o of LAUNCH_OPTIONS) {
  // (-nomonsters, -respawn, -fast on the command line tick them for this visit)
  launchOptions[o] = commandLine.options[o];
  $(`opt-${o}`).checked = launchOptions[o] === 1;
  $(`opt-${o}`).addEventListener('change', (e) => {
    launchOptions[o] = e.target.checked ? 1 : 0;
    // (a game alone on screen starts again with them; a netgame keeps the host's)
    if (db && map && !title && !net) startMap(map.name, true).catch((err) => setStatus(err.message, true));
  });
}
// demos: record, stop, play, download, load
$('demo-record').addEventListener('click', () => startRecording().catch((err) => setStatus(err.message, true)));
$('demo-stop').addEventListener('click', () => { if (recorder) finishRecording(); else if (demoPlayer) endPlayback('stopped'); });
$('demo-play').addEventListener('click', () => lastDemo && playDemo(lastDemo).catch((err) => setStatus(err.message, true)));
$('demo-download').addEventListener('click', () => {
  if (!lastDemo) return;
  const blob = new Blob([JSON.stringify(lastDemo)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `firebird-doom-${lastDemo.map}-${lastDemo.date.slice(0, 19).replace(/[:T]/g, '-')}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
});
$('demo-file').addEventListener('change', async (e) => {
  const f = e.target.files[0];
  if (!f) return;
  try {
    const bytes = new Uint8Array(await f.arrayBuffer());
    // DOOM's own (.lmp: a version byte, or a v1.2 skill, first), or the port's (JSON)
    if (/\.lmp$/i.test(f.name) || bytes[0] !== 0x7b) {
      await playLmp(bytes, f.name);
    } else {
      const demo = JSON.parse(new TextDecoder().decode(bytes));
      lastDemo = demo;
      updateDemoButtons();
      await playDemo(demo);
    }
  } catch (err) { setStatus(`Can't read that demo: ${err.message}`, true); }
  e.target.value = '';
});
// saves as a file: this WAD's six slots out, a file's filled slots in
$('saves-export').addEventListener('click', async () => {
  if (!wadKey) return;
  try {
    const file = await exportSaves(saves, wadKey);
    if (!file.slots.some(Boolean)) { setStatus('No saves to export for this WAD yet.'); return; }
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([JSON.stringify(file)], { type: 'application/json' }));
    a.download = `firebird-doom-saves-${wadKey.split('|')[0].replace(/\.wad$/i, '')}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  } catch (err) { setStatus(`Couldn't export the saves: ${err.message}`, true); }
});
$('saves-file').addEventListener('change', async (e) => {
  const f = e.target.files[0];
  e.target.value = '';
  if (!f || !wadKey) return;
  try {
    const file = JSON.parse(await f.text());
    const over = (file.slots ?? []).map((r, i) => (r && saveSlots[i] ? i + 1 : 0)).filter(Boolean);
    if (over.length && !window.confirm(`Replace the saves in slot${over.length > 1 ? 's' : ''} ${over.join(', ')}?`)) return;
    const written = await importSaves(saves, wadKey, file);
    await refreshSlots();
    setStatus(`Imported ${written.length} save${written.length === 1 ? '' : 's'} (slot${written.length === 1 ? '' : 's'} ${written.map((i) => i + 1).join(', ')}).`);
  } catch (err) { setStatus(`Can't import those saves: ${err.message}`, true); }
});
updateDemoButtons();
$('display').value = settings.display;
$('smooth').checked = settings.smooth;
$('mousey').checked = settings.mousey;
$('mousey').addEventListener('change', (e) => { settings.mousey = e.target.checked; saveSettings(); });
$('display').addEventListener('change', (e) => {
  settings.display = e.target.value;
  saveSettings();
  applyDisplay();
});
$('smooth').addEventListener('change', (e) => {
  settings.smooth = e.target.checked;
  saveSettings();
  presenter?.setSmooth(settings.smooth);
});
applyDisplay();
$('detail').addEventListener('change', async (e) => {
  settings.detail = e.target.value;
  saveSettings();
  await applyView();
});

boot();
